import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  gstAppliesToDocumentFor,
  isPreGstRegistrationFor,
} from "../../../../shared/utils/gstApplicability";
import { GstPreRegistrationNotice } from "../../../../shared/presentation/components/GstPreRegistrationNotice";
import { LocationPicker } from "../../../../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  useToast,
  ErrorBanner,
} from "../../../../shared/presentation/components";
import { useErrorHandling } from "../../../../shared/presentation/hooks";
import { Loader } from "../../../../ui/components/Loader";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { TextField } from "../../../../ui/components/TextField";
import { Badge } from "../../../../ui/components/Badge";
import { StatusBadgeForCreditAndDebitNotes } from "../../../../ui/components/StatusBadgeForCreditAndDebitNotes";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import {
  FeatherX,
  FeatherSave,
  FeatherCheck,
  FeatherCalendar,
  FeatherPlus,
  FeatherChevronDown,
  FeatherEdit2,
  FeatherMessageCircle,
  FeatherArrowLeft,
  FeatherTrash2,
  FeatherExternalLink,
} from "@subframe/core";
import { EditLineItemForCreditNoteComponent } from "../components";
import DocumentViewer from "../../../../components/DocumentViewer";
import QueryRaiseModal from "../../../../shared/queries/presentation/components/QueryRaiseModal";
import QueryDetailModal from "../../../../shared/queries/presentation/components/QueryDetailModal";
import QueriesListForDocument from "../../../../shared/queries/presentation/components/QueriesListForDocument";
import ActivityLogComponent from "../../../../components/ActivityLogComponent";
import { SearchableDropdown } from "../../../../shared/presentation/components";
import {
  fetchCurrencies,
  Currency,
} from "../../../../shared/data/api/currenciesApi";
import { getCompany } from "../../../../shared/data/storage/authStorage";
import {
  getApproveAndPostQueriesMessage,
  getDeleteAfterPushPermissionDeniedMessage,
  getPushPermissionDeniedMessage,
  getReviewRequestPushReason,
  getReviewRequestUpdateAfterPostReason,
  getStoredAccountingBackend,
  getUpdateAfterPostPermissionDeniedMessage,
} from "../../../../config/accounting";
import {
  useCreditNoteReview,
  useCreditNoteOperations,
  useCustomersForCreditNote,
  useCustomerInvoices,
  useCreditNoteQueries,
} from "../hooks";
import { useVendorsForCreditNote } from "../hooks/useVendorsForCreditNote";
import { useVendorBills } from "../hooks/useVendorBills";
import { useZohoInvoiceDetails } from "../hooks/useZohoInvoiceDetails";
import { getCustomerById } from "../../../../services/customerApi";
import { getVendorById } from "../../../../services/vendorApi";
import {
  CreditNoteDataEntity,
  CreditNoteLineItemEntity,
  CreditNoteEntity,
  CreditsAppliedResultEntity,
  CreditAppliedEntity,
} from "../../domain/entities/CreditNoteEntity";
import { CustomerListItem } from "../hooks/useCustomersForCreditNote";
import { VendorListItem } from "../hooks/useVendorsForCreditNote";
import { VendorBillListItem } from "../hooks/useVendorBills";
import { InvoiceListItemEntity } from "../../../../shared/domain/entities/InvoiceListItemEntity";
import { Alert } from "../../../../ui/components/Alert";

// Permissions and Review
import {
  MODULE_CODES,
  ACTION_CODES,
  type ActionCode,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import {
  ReviewStatusBanner,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
} from "../../../../shared/presentation/components";
import { useEntityReviewStatus } from "../../../../shared/presentation/hooks/useEntityReviewStatus";
import { useCreditDebitNoteReviewData } from "../../../../shared/presentation/hooks";
import {
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../../../../shared/presentation/hooks";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
} from "../../../../shared/utils/reviewWorkflow";
import { dateToMonthYear } from "../../../../shared/utils/date";
import { CreditNoteRepositoryImpl } from "../../data/repositories/CreditNoteRepositoryImpl";
import { useAuth } from "../../../../features/auth/presentation/hooks/useAuth";

/**
 * ============================================================================
 * PERMISSION CHECKS DOCUMENTATION
 * ============================================================================
 *
 * Screen: Credit Note Review/Detail Page
 * File: frontend/src/features/credit/presentation/pages/CreditNoteReviewPage.tsx
 *
 * API Endpoints Used:
 * 1. GET /api/permissions/me
 *    - Purpose: Fetch all user permissions
 *    - Called: Once on component mount via usePermissions hook
 *
 * 2. POST /api/reviews (if update_after_push requires review)
 *    - Purpose: Create review request for updating posted credit note
 *    - Called: When user tries to edit a posted credit note and review is required
 *
 * 3. GET /api/reviews/check-approved (if update_after_push requires review)
 *    - Purpose: Check if update_after_push has approved review
 *    - Called: Before allowing edit when credit note is posted and review required
 *
 * Request Body: N/A (GET request for permissions)
 *
 * Response Structure for GET /api/permissions/me:
 * {
 *   "success": true,
 *   "data": {
 *     "permissions": {
 *       "credit_note": {
 *         "create": { "granted": true, "requires_review": false },
 *         "update": { "granted": true, "requires_review": false },
 *         "update_after_push": { "granted": true, "requires_review": true }
 *       }
 *     }
 *   },
 *   "message": "Permissions retrieved successfully"
 * }
 *
 * Interface Used:
 * - PermissionsMap from frontend/src/features/admin/domain/entities/PermissionEntity.ts
 * - CreateReviewRequestDTO from frontend/src/shared/utils/reviewWorkflow.ts
 * - CreateReviewResponseDTO from frontend/src/shared/utils/reviewWorkflow.ts
 * - CheckApprovedReviewResponseDTO from frontend/src/shared/utils/reviewWorkflow.ts
 *
 * Permission Checks Implemented:
 * 1. credit_note.create
 *    - Checked via: hasPermission(MODULE_CODES.CREDIT_NOTE, ACTION_CODES.CREATE)
 *    - Used for: Manual creation (when isManualCreation === true)
 *    - Location: Save/Create button when creating new credit note
 *
 * 2. credit_note.update
 *    - Checked via: getPermissionInfo(MODULE_CODES.CREDIT_NOTE, ACTION_CODES.UPDATE)
 *    - Used for: Edit button, auto-save, "Save and Go Back" button (when NOT posted)
 *
 * 3. credit_note.update_after_push
 *    - Checked via: getPermissionInfo(MODULE_CODES.CREDIT_NOTE, ACTION_CODES.UPDATE_AFTER_PUSH)
 *    - Used for: Edit button, auto-save, "Save and Go Back" button (when IS posted)
 *    - Review Required: Yes (if requires_review is true)
 *
 * Review Workflow: Same as InvoiceReviewPage
 * ============================================================================
 */

import {
  COUNTRIES,
  findCountryByCode,
} from "../../../../shared/constants/countries";
import {
  INDIAN_STATES,
  findIndianStateByCode,
} from "../../../../shared/constants/indianStates";

interface CreditNoteReviewPageProps {}

const omitSupplyFields = <T extends Record<string, any> | null | undefined>(
  data: T,
): T => {
  if (!data) return data;
  const {
    place_of_supply: _placeOfSupply,
    destination_of_supply: _destinationOfSupply,
    source_of_supply: _sourceOfSupply,
    ...rest
  } = data;
  return rest as T;
};

// Create repository instance for document fetching
const creditNoteRepository = new CreditNoteRepositoryImpl();

const CreditNoteReviewPage: React.FC<CreditNoteReviewPageProps> = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const accountingBackend = getStoredAccountingBackend();

  // Extract data from navigation state
  const documentId = location.state?.documentId || id || null;
  const isManualCreation = location.state?.isManualCreation || !documentId;
  const isEditMode = location.state?.isEdit || false;
  const initialSignedUrl: string | null = location.state?.signedUrl || null;
  const initialFileName = location.state?.fileName || null;
  const initialCreditNoteJson: CreditNoteDataEntity | null =
    location.state?.creditNoteJson || null;
  const initialCreditNoteDocument: CreditNoteEntity | null =
    location.state?.creditNoteDocument || null;

  // Fetch credit note document function for the hook
  const fetchCreditNoteDocument = useCallback(
    async (id: string): Promise<CreditNoteEntity> => {
      return await creditNoteRepository.getCreditNoteById(id);
    },
    [],
  );

  // Check if credit note is posted to Zoho
  const checkCreditNotePostedToZoho = useCallback(
    (doc: CreditNoteEntity): boolean => {
      return (
        doc.status === "1" &&
        doc.posted_to_zoho_date !== null &&
        doc.posted_to_zoho_date !== undefined
      );
    },
    [],
  );

  // Use the new review data hook
  const reviewData = useCreditDebitNoteReviewData({
    moduleCode: MODULE_CODES.CREDIT_NOTE,
    entityType: "credit_note",
    documentId,
    fetchDocument: fetchCreditNoteDocument,
    isPostedToZoho: checkCreditNotePostedToZoho,
    initialDocument: initialCreditNoteDocument,
    includeDeleteAfterPush: true, // Credit notes have delete_after_push
  });

  // Extract values from reviewData
  const {
    isDataLoading,
    document: creditNoteDocumentFromHook,
    isPostedToZoho,
    canCreate,
    canUpdateAfterPush,
    canPushToZoho,
    canDeleteAfterPush,
    needsReviewForCreate,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    needsReviewForDeleteAfterPush,
  } = reviewData;

  // Get credit note document details (for signedUrl, fileName, etc.)
  const creditNoteDocument =
    creditNoteDocumentFromHook as CreditNoteEntity | null;
  const signedUrl = creditNoteDocument?.signed_url || initialSignedUrl;
  const fileName = creditNoteDocument?.original_filename || initialFileName;
  const fetchedCreditNoteJson = creditNoteDocument?.json || null;

  // Store the original document when first loaded (for comparison)
  const [originalCreditNoteJson, setOriginalCreditNoteJson] =
    useState<CreditNoteDataEntity | null>(initialCreditNoteJson);

  // Closed month confirm modal (when non-Admin/CFO tries to save or approve in closed month)
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);
  const [pendingClosedMonthSource, setPendingClosedMonthSource] = useState<
    "approve" | "delete" | null
  >(null);

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingReviewAction, setPendingReviewAction] = useState<{
    changeDetails?: ChangeDetails;
    disableSubmit?: boolean;
    actionCode?: ActionCode;
  } | null>(null);

  // Closed month: derive month from credit note date (synced when creditNoteJson.date changes)
  const [dateForClosedCheck, setDateForClosedCheck] = useState(
    initialCreditNoteJson?.date ?? fetchedCreditNoteJson?.date ?? "",
  );
  const monthForClosedCheck = dateForClosedCheck
    ? dateToMonthYear(dateForClosedCheck)
    : "";
  const { isClosed: isMonthClosed } = useIsMonthClosed(
    monthForClosedCheck || undefined,
  );
  const isAdminOrCFO = useIsAdminOrCFO();

  // Review status (for banner display)
  const { review } = useEntityReviewStatus(
    documentId ? "credit_note" : null,
    documentId ? parseInt(documentId) : null,
  );

  // Check if review is pending (blocks all actions)
  const isReviewPending = review?.status === "pending";

  // State for showing approved banner temporarily (auto-dismisses after 5 seconds)
  const [showApprovedBanner, setShowApprovedBanner] = useState(false);
  const approvedBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Show approved banner when review is approved (on mount or when review changes to approved)
  useEffect(() => {
    if (review?.status === "approved") {
      setShowApprovedBanner(true);

      // Clear any existing timeout
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }

      // Auto-dismiss after 5 seconds
      approvedBannerTimeoutRef.current = setTimeout(() => {
        setShowApprovedBanner(false);
      }, 5000);
    } else {
      setShowApprovedBanner(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }
    };
  }, [review?.status, review?.id]);

  // Store the original posted total amount for validation
  const [originalPostedTotal, setOriginalPostedTotal] = useState<number | null>(
    null,
  );

  // Credits applied state
  const [creditsApplied, setCreditsApplied] =
    useState<CreditsAppliedResultEntity | null>(null);

  // Calculated values for credits applied (computed after API response)
  const [creditsAppliedCalculations, setCreditsAppliedCalculations] = useState<{
    invoiceBillTotal: number;
    totalCreditsApplied: number;
    remainingBalance: number;
  } | null>(null);

  // Delete credit note application hook

  // Track all changes made by the user (accumulated as they edit)
  const [trackedChanges, setTrackedChanges] = useState<
    Array<{
      field: string;
      old: string;
      new: string;
    }>
  >([]);

  // Helper functions for change tracking
  const formatValueForTracking = useCallback((value: any): string => {
    if (value === null || value === undefined) return "empty";
    if (value === "") return "empty";
    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "number") return String(value);
    return String(value);
  }, []);

  const formatFieldName = useCallback((fieldName: string): string => {
    const formatted = fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return formatted;
  }, []);

  /**
   * Build field_changes from current credit note form (for closed-month when trackedChanges is empty).
   * Includes all credit note form entries: document header and every line item field with readable names.
   * Does not include internal "type" field (Zoho line item type).
   */
  const getFieldChangesFromCreditNoteForm = useCallback(
    (
      json: CreditNoteDataEntity | null,
    ): Array<{ field: string; old: string; new: string }> => {
      const changes: Array<{ field: string; old: string; new: string }> = [];
      if (!json) return changes;
      const toStr = (v: any) =>
        v == null || v === ""
          ? ""
          : typeof v === "object"
            ? JSON.stringify(v)
            : String(v);

      // Document header fields (all form-relevant entries)
      const headerFields: Array<{
        key: keyof CreditNoteDataEntity;
        label: string;
      }> = [
        { key: "date", label: "Date" },
        { key: "customer_name", label: "Customer Name" },
        { key: "vendor_name", label: "Vendor Name" },
        { key: "creditnote_number", label: "Credit Note Number" },
        { key: "gst_treatment", label: "GST Treatment" },
        { key: "gst_no", label: "GST No" },
        { key: "currency_code", label: "Currency" },
        { key: "currency_symbol", label: "Currency Symbol" },
        { key: "reference_number", label: "Reference Number" },
        { key: "notes", label: "Notes" },
        { key: "narration", label: "Narration" },
        { key: "sub_total", label: "Sub Total" },
        { key: "tax_total", label: "Tax Total" },
        { key: "total", label: "Total" },
      ];
      headerFields.forEach(({ key, label }) => {
        const val = json[key];
        if (val !== undefined && val !== null && val !== "")
          changes.push({ field: label, old: "", new: toStr(val) });
      });

      // Line items: all form-relevant fields with readable labels (no "type")
      const lineItemFields: Array<{ key: string; label: string }> = [
        { key: "name", label: "Name" },
        { key: "description", label: "Description" },
        { key: "account_name", label: "Account Name" },
        { key: "quantity", label: "Quantity" },
        { key: "rate", label: "Rate" },
        { key: "tax_name", label: "Tax Name" },
        { key: "hsn_or_sac", label: "HSN Or SAC" },
        { key: "tax_percentage", label: "Tax Percentage" },
        { key: "tax_amount", label: "Tax Amount" },
        { key: "item_total", label: "Item Total" },
        {
          key: "item_total_inclusive_of_tax",
          label: "Item Total Inclusive Of Tax",
        },
        { key: "tds_tax_name", label: "TDS Tax Name" },
        { key: "tds_tax_percentage", label: "TDS Tax Percentage" },
        { key: "tds_tax_amount", label: "TDS Tax Amount" },
      ];
      const items = json.line_items || [];
      items.forEach((item: any, index: number) => {
        const lineNum = index + 1;
        lineItemFields.forEach(({ key, label }) => {
          const val = item[key];
          if (val === undefined || val === null) return;
          const str = toStr(val);
          if (str === "") return;
          changes.push({
            field: `Line Item ${lineNum}: ${label}`,
            old: "",
            new: str,
          });
        });
      });
      return changes;
    },
    [],
  );

  // ZOUR-101 — an existing note must be IN STATE before anything may auto-save.
  //
  // `creditNoteJson` starts as the blank default until the GET returns and the
  // effect below copies the fetched JSON into it. In that window `isPostedToZoho`
  // is still false, so auto-save was ON, and the totals effect fires on mount
  // (`tax_amount` undefined !== 0) and queued the BLANK note for saving. A GET
  // slower than the 1 s debounce then POSTed the empty default over the real
  // note (local note 4 lost its content this way; note 5 survived by ordering).
  //
  // `originalCreditNoteJson` is the marker: it is null until the fetched JSON has
  // been copied into state (or arrived via navigation state), and it flips in
  // the SAME batch as `setCreditNoteJson`/`setLineItems`, so there is no render
  // where auto-save is on but state is still the default. A row whose stored
  // `json` is genuinely empty has nothing to lose, so it is let through.
  // Manual creation (`/new`, no documentId) never auto-saves and is unchanged.
  const isLoadedNoteHydrated =
    !documentId ||
    originalCreditNoteJson !== null ||
    (creditNoteDocument !== null && !creditNoteDocument.json);

  // Remove permission checks - only disable auto-save for posted documents
  // Check both isPostedToZoho flag AND document status directly (defensive check)
  const disableAutoSave =
    isPostedToZoho ||
    (creditNoteDocument?.status === "1" &&
      creditNoteDocument?.posted_to_zoho_date !== null &&
      creditNoteDocument?.posted_to_zoho_date !== undefined) ||
    !isLoadedNoteHydrated;

  // Initialize credit note review hook (handles JSON state and auto-save)
  const {
    creditNoteJson,
    setCreditNoteJson,
    isAutoSaving,
    lastSaved,
    updateCreditNoteJson,
  } = useCreditNoteReview(
    documentId,
    initialCreditNoteJson,
    disableAutoSave,
    () => false,
    () => {},
  );

  // Sync date for closed-month check when credit note date changes
  useEffect(() => {
    if (creditNoteJson?.date) {
      setDateForClosedCheck(creditNoteJson.date);
    }
  }, [creditNoteJson?.date]);

  // Enhanced updateCreditNoteJson that tracks changes
  const updateCreditNoteJsonWithTracking = useCallback(
    (updates: Partial<CreditNoteDataEntity>) => {
      // Sync closed-month check date immediately when date changes (avoids lag before useEffect)
      if (updates.date !== undefined) {
        setDateForClosedCheck(updates.date);
      }
      // Update the JSON first
      updateCreditNoteJson(updates);

      // Track changes by comparing with original document
      if (originalCreditNoteJson) {
        setTrackedChanges((prevTrackedChanges) => {
          const changesToTrack: Array<{
            field: string;
            old: string;
            new: string;
          }> = [];
          const updatedChanges = [...prevTrackedChanges];

          // Customer/Vendor-related fields that should be removed when customer_id/vendor_id changes
          const customerAutoUpdateFields = [
            "customer_id",
            "customer_name",
            "gst_no",
          ];
          const vendorAutoUpdateFields = [
            "vendor_id",
            "vendor_name",
            "gst_no",
          ];

          // Check if customer_id is being changed
          const isCustomerIdChanging =
            updates.customer_id !== undefined &&
            originalCreditNoteJson.customer_id !== updates.customer_id;

          // Check if vendor_id is being changed
          const isVendorIdChanging =
            updates.vendor_id !== undefined &&
            originalCreditNoteJson.vendor_id !== updates.vendor_id;

          // If customer_id is changing, remove all customer-related tracked changes
          if (isCustomerIdChanging) {
            customerAutoUpdateFields.forEach((field) => {
              const index = updatedChanges.findIndex(
                (change) =>
                  change.field === field ||
                  change.field === formatFieldName(field),
              );
              if (index >= 0) {
                updatedChanges.splice(index, 1);
              }
            });

            // Track customer change
            const oldCustomerName =
              originalCreditNoteJson.customer_name || "Unknown";
            const newCustomerName =
              (updates.customer_name as string) || "Unknown";

            changesToTrack.push({
              field: "Customer",
              old: oldCustomerName,
              new: newCustomerName,
            });
          }

          // If vendor_id is changing, remove all vendor-related tracked changes
          if (isVendorIdChanging) {
            vendorAutoUpdateFields.forEach((field) => {
              const index = updatedChanges.findIndex(
                (change) =>
                  change.field === field ||
                  change.field === formatFieldName(field),
              );
              if (index >= 0) {
                updatedChanges.splice(index, 1);
              }
            });

            // Track vendor change
            const oldVendorName =
              originalCreditNoteJson.vendor_name || "Unknown";
            const newVendorName = (updates.vendor_name as string) || "Unknown";

            changesToTrack.push({
              field: "Vendor",
              old: oldVendorName,
              new: newVendorName,
            });
          }

          // Process all updates
          Object.keys(updates).forEach((key) => {
            // Skip line_items - handled separately in handleSaveLineItem
            if (key === "line_items") {
              return;
            }

            // Skip customer_id/vendor_id if we already handled it above
            if (
              (isCustomerIdChanging && key === "customer_id") ||
              (isVendorIdChanging && key === "vendor_id")
            ) {
              return;
            }

            // Skip auto-update fields if customer/vendor is changing
            if (
              (isCustomerIdChanging &&
                customerAutoUpdateFields.indexOf(key) !== -1) ||
              (isVendorIdChanging && vendorAutoUpdateFields.indexOf(key) !== -1)
            ) {
              return;
            }

            const oldValue = (originalCreditNoteJson as any)[key];
            const newValue = (updates as any)[key];

            // Only track if value actually changed from original
            if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
              // Format field name for display
              const formattedFieldName = formatFieldName(key);

              // Check if this field is already tracked
              const existingChangeIndex = updatedChanges.findIndex(
                (change) => change.field === formattedFieldName,
              );

              if (existingChangeIndex >= 0) {
                // Update existing tracked change
                updatedChanges[existingChangeIndex] = {
                  field: formattedFieldName,
                  old: formatValueForTracking(oldValue),
                  new: formatValueForTracking(newValue),
                };
              } else {
                // Add new tracked change
                changesToTrack.push({
                  field: formattedFieldName,
                  old: formatValueForTracking(oldValue),
                  new: formatValueForTracking(newValue),
                });
              }
            } else {
              // If value matches original, remove it from tracked changes (user reverted)
              const formattedFieldName = formatFieldName(key);
              const existingChangeIndex = updatedChanges.findIndex(
                (change) => change.field === formattedFieldName,
              );
              if (existingChangeIndex >= 0) {
                updatedChanges.splice(existingChangeIndex, 1);
              }
            }
          });

          // Return updated array: existing changes (possibly modified) + new changes
          return [...updatedChanges, ...changesToTrack];
        });
      }
    },
    [
      updateCreditNoteJson,
      originalCreditNoteJson,
      formatValueForTracking,
      formatFieldName,
    ],
  );

  // Ref to track latest creditNoteJson for review requests (to avoid stale state)
  const creditNoteJsonRef = useRef<CreditNoteDataEntity | null>(
    initialCreditNoteJson,
  );

  useEffect(() => {
    creditNoteJsonRef.current = creditNoteJson;
  }, [creditNoteJson]);

  // Update original when document is fetched
  useEffect(() => {
    if (
      fetchedCreditNoteJson &&
      (!originalCreditNoteJson ||
        originalCreditNoteJson.creditnote_number !==
          fetchedCreditNoteJson.creditnote_number)
    ) {
      setOriginalCreditNoteJson(fetchedCreditNoteJson);
      setCreditNoteJson(fetchedCreditNoteJson);
      // CRITICAL: For posted documents, immediately set lineItems from database JSON
      // This ensures we use the saved line items, not invoice/bill line items
      if (
        fetchedCreditNoteJson.line_items &&
        fetchedCreditNoteJson.line_items.length > 0
      ) {
        setLineItems(fetchedCreditNoteJson.line_items);
      }
      // Reset tracked changes when new document is loaded
      setTrackedChanges([]);
    }
  }, [fetchedCreditNoteJson, originalCreditNoteJson, setCreditNoteJson]);

  // Initialize credit note operations hook
  const {
    loading: operationsLoading,
    error: operationsError,
    createZohoCreditNote,
    updateZohoCreditNote,
    rejectCreditNote,
    saveCreditNoteAsDraft,
    clearError: clearOperationsError,
    deleteCreditNote,
  } = useCreditNoteOperations();

  const {
    invoiceDetails,
    loading: invoiceDetailsLoading,
    error: invoiceDetailsError,
    fetchInvoiceDetails,
    clearError: clearInvoiceDetailsError,
    reset: resetInvoiceDetails,
  } = useZohoInvoiceDetails();

  // Initialize customer hooks
  const { fetchCustomers: originalFetchCustomers, loading: customersLoading } =
    useCustomersForCreditNote();

  const { fetchInvoices: originalFetchInvoices, loading: invoicesLoading } =
    useCustomerInvoices();

  // Wrapper for fetchInvoices that pushes selected invoice to top
  const fetchInvoices = useCallback(
    async (
      customerZohoId: string,
      searchTerm: string,
      limit: number,
      offset: number,
      selectedInvoiceId?: string,
      selectedInvoiceNumber?: string,
    ) => {
      const result = await originalFetchInvoices(
        customerZohoId,
        searchTerm,
        limit,
        offset,
      );

      // Filter out invoices with zero balance (cannot apply credit notes to fully paid invoices)
      const filteredItems = result.items.filter(
        (invoice) => (invoice.balance ?? 0) > 0,
      );

      // Always ensure selected invoice is in results (if provided)
      if (
        selectedInvoiceId &&
        selectedInvoiceNumber &&
        offset === 0 &&
        !searchTerm
      ) {
        const selectedKey = selectedInvoiceId;

        // Check if selected invoice is already in the filtered results
        const selectedIndex = filteredItems.findIndex(
          (item) =>
            (item.invoice_id || item.id?.toString() || "") === selectedKey,
        );

        if (selectedIndex >= 0) {
          // Selected invoice is in results, move it to top
          if (selectedIndex > 0) {
            const itemsCopy = [...filteredItems];
            const [selectedItem] = itemsCopy.splice(selectedIndex, 1);
            return {
              items: [selectedItem, ...itemsCopy],
              hasMore: result.hasMore,
            };
          }
          // Already at top, return as is
          return {
            items: filteredItems,
            hasMore: result.hasMore,
          };
        } else {
          // Selected invoice not found in results
          // Check cache first
          const cachedInvoice = selectedInvoiceCacheRef.current;
          if (cachedInvoice) {
            const cachedKey =
              cachedInvoice.invoice_id || cachedInvoice.id?.toString() || "";
            if (cachedKey === selectedKey) {
              return {
                items: [cachedInvoice, ...filteredItems],
                hasMore: result.hasMore,
              };
            }
          }

          // Try to fetch invoice details from Zoho API
          try {
            // Fetch invoice details - this will automatically set invoiceDetails state
            // which will trigger the useEffect to process line items automatically
            await fetchInvoiceDetails(selectedInvoiceId);
            // Note: invoiceDetails state will be updated by fetchInvoiceDetails,
            // and the useEffect will automatically process line items when state updates.
            // For the dropdown, we'll create a synthetic item since we can't access
            // the state immediately (React state updates are async)
          } catch (error) {
            console.warn(
              "Could not fetch selected invoice from Zoho API, using synthetic item:",
              error,
            );
          }

          // Fallback: Create synthetic invoice
          const syntheticInvoice: InvoiceListItemEntity = {
            invoice_id: selectedInvoiceId,
            id: parseInt(selectedInvoiceId) || null,
            invoice_number: selectedInvoiceNumber,
            date: "",
            total: 0,
            tax_total: 0,
            balance: 0,
            currency_id: "",
            exchange_rate: 0,
            unprocessed_payment_amount: 0,
            currency_code: "",
            currency_symbol: "",
            is_tds_applied: 0,
            tds_tax_id: null,
            tds_section: null,
            tds_tax_amount: 0,
            tds_tax_percentage: 0,
            tds_tax_name: null,
          };
          return {
            items: [syntheticInvoice, ...filteredItems],
            hasMore: result.hasMore,
          };
        }
      }

      return {
        items: filteredItems,
        hasMore: result.hasMore,
      };
    },
    [originalFetchInvoices, fetchInvoiceDetails],
  );

  // Initialize vendor hooks
  const { fetchVendors: originalFetchVendors, loading: vendorsLoading } =
    useVendorsForCreditNote();

  // Cache for selected customer and vendor to push to top
  const selectedCustomerCacheRef = useRef<CustomerListItem | null>(null);
  const selectedVendorCacheRef = useRef<VendorListItem | null>(null);

  // Cache for selected invoice and bill to push to top
  const selectedInvoiceCacheRef = useRef<InvoiceListItemEntity | null>(null);
  const selectedBillCacheRef = useRef<VendorBillListItem | null>(null);

  // Wrapper for fetchCustomers that pushes selected customer to top
  const fetchCustomers = useCallback(
    async (
      searchTerm: string,
      limit: number,
      offset: number,
      selectedCustomerId?: string,
      selectedCustomerName?: string,
    ) => {
      const result = await originalFetchCustomers(searchTerm, limit, offset);

      // Always ensure selected customer is in results (if provided)
      if (
        selectedCustomerId &&
        selectedCustomerName &&
        offset === 0 &&
        !searchTerm
      ) {
        const selectedKey = selectedCustomerId;

        // Check if selected customer is already in the results
        const selectedIndex = result.items.findIndex(
          (item) =>
            (item.zoho_customerid || item.customer_id || "") === selectedKey,
        );

        if (selectedIndex >= 0) {
          // Selected customer is in results, move it to top
          if (selectedIndex > 0) {
            const itemsCopy = [...result.items];
            const [selectedItem] = itemsCopy.splice(selectedIndex, 1);
            return {
              items: [selectedItem, ...itemsCopy],
              hasMore: result.hasMore,
            };
          }
          // Already at top, return as is
          return result;
        } else {
          // Selected customer not found in results
          // Check cache first
          const cachedCustomer = selectedCustomerCacheRef.current;
          if (cachedCustomer) {
            const cachedKey =
              cachedCustomer.zoho_customerid ||
              cachedCustomer.customer_id ||
              "";
            if (cachedKey === selectedKey) {
              return {
                items: [cachedCustomer, ...result.items],
                hasMore: result.hasMore,
              };
            }
          }

          // Try to fetch from backend
          try {
            const response = await getCustomerById(selectedCustomerId);
            if (response.data) {
              const fetchedCustomer: CustomerListItem = {
                id: 0, // API doesn't return id
                zoho_customerid: response.data.zoho_customerid || "",
                customer_id: response.data.customer_id || "",
                customer_name: response.data.customer_name || "",
                company_name: response.data.customer_name || "", // API doesn't have company_name, use customer_name
                email: response.data.contact_email_1 || "",
                phone: response.data.contact_number_1 || "",
                status: response.data.status || "",
                billing_address: response.data.billing_address || null,
                shipping_address: response.data.shipping_address || null,
                currency_code:
                  response.data.currency_code ??
                  response.data.currency_id ??
                  "",
                currency_symbol: response.data.currency_symbol ?? "",
                payment_terms: response.data.payment_terms || "",
                credit_limit: response.data.credit_limit || 0,
                created_at: response.data.created_at || "",
                updated_at: response.data.updated_at || "",
              };
              // Store in cache
              selectedCustomerCacheRef.current = fetchedCustomer;
              return {
                items: [fetchedCustomer, ...result.items],
                hasMore: result.hasMore,
              };
            }
          } catch (error) {
            console.warn(
              "Could not fetch selected customer from backend, using synthetic item:",
              error,
            );
          }

          // Fallback: Create synthetic customer
          const syntheticCustomer: CustomerListItem = {
            id: 0,
            zoho_customerid: selectedCustomerId,
            customer_id: selectedCustomerId,
            customer_name: selectedCustomerName,
            company_name: selectedCustomerName,
            email: "",
            phone: "",
            status: "",
            billing_address: null,
            shipping_address: null,
            currency_code: "",
            currency_symbol: "",
            payment_terms: "",
            credit_limit: 0,
            created_at: "",
            updated_at: "",
          };
          return {
            items: [syntheticCustomer, ...result.items],
            hasMore: result.hasMore,
          };
        }
      }

      return result;
    },
    [originalFetchCustomers],
  );

  // Wrapper for fetchVendors that pushes selected vendor to top
  const fetchVendors = useCallback(
    async (
      searchTerm: string,
      limit: number,
      offset: number,
      selectedVendorId?: string,
      selectedVendorName?: string,
    ) => {
      const result = await originalFetchVendors(searchTerm, limit, offset);

      // Always ensure selected vendor is in results (if provided)
      if (
        selectedVendorId &&
        selectedVendorName &&
        offset === 0 &&
        !searchTerm
      ) {
        const selectedKey = selectedVendorId;

        // Check if selected vendor is already in the results
        const selectedIndex = result.items.findIndex(
          (item) => (item.zoho_vendorid || item.vendorid || "") === selectedKey,
        );

        if (selectedIndex >= 0) {
          // Selected vendor is in results, move it to top
          if (selectedIndex > 0) {
            const itemsCopy = [...result.items];
            const [selectedItem] = itemsCopy.splice(selectedIndex, 1);
            return {
              items: [selectedItem, ...itemsCopy],
              hasMore: result.hasMore,
            };
          }
          // Already at top, return as is
          return result;
        } else {
          // Selected vendor not found in results
          // Check cache first
          const cachedVendor = selectedVendorCacheRef.current;
          if (cachedVendor) {
            const cachedKey =
              cachedVendor.zoho_vendorid || cachedVendor.vendorid || "";
            if (cachedKey === selectedKey) {
              return {
                items: [cachedVendor, ...result.items],
                hasMore: result.hasMore,
              };
            }
          }

          // Try to fetch from backend
          try {
            const response = await getVendorById(selectedVendorId);
            if (response.data) {
              const fetchedVendor: VendorListItem = {
                id: response.data.vendorid || 0,
                zoho_vendorid: response.data.zoho_vendorid || "",
                vendorid: String(response.data.vendorid || ""),
                vendor_legal_name: response.data.vendor_legal_name || "",
              };
              // Store in cache
              selectedVendorCacheRef.current = fetchedVendor;
              return {
                items: [fetchedVendor, ...result.items],
                hasMore: result.hasMore,
              };
            }
          } catch (error) {
            console.warn(
              "Could not fetch selected vendor from backend, using synthetic item:",
              error,
            );
          }

          // Fallback: Create synthetic vendor
          const syntheticVendor: VendorListItem = {
            id: 0,
            zoho_vendorid: selectedVendorId,
            vendorid: selectedVendorId,
            vendor_legal_name: selectedVendorName,
          };
          return {
            items: [syntheticVendor, ...result.items],
            hasMore: result.hasMore,
          };
        }
      }

      return result;
    },
    [originalFetchVendors],
  );

  const {
    fetchBills: originalFetchVendorBills,
    fetchBillDetails,
    loading: vendorBillsLoading,
  } = useVendorBills();

  // Bill details state (for vendor credits) - defined before wrapper that uses it
  const [billDetails, setBillDetails] = useState<any | null>(null);
  const [billDetailsLoading, setBillDetailsLoading] = useState(false);
  const [billDetailsError, setBillDetailsError] = useState<string | null>(null);

  // Wrapper for fetchVendorBills that pushes selected bill to top
  const fetchVendorBills = useCallback(
    async (
      vendorZohoId: string,
      searchTerm: string,
      limit: number,
      offset: number,
      selectedBillId?: string,
      selectedBillNumber?: string,
    ) => {
      const result = await originalFetchVendorBills(
        vendorZohoId,
        searchTerm,
        limit,
        offset,
      );

      // Filter out bills with zero balance (cannot apply vendor credits to fully paid bills)
      const filteredItems = result.items.filter(
        (bill) => (bill.balance ?? 0) > 0,
      );

      // Always ensure selected bill is in results (if provided)
      if (selectedBillId && selectedBillNumber && offset === 0 && !searchTerm) {
        const selectedKey = selectedBillId;

        // Check if selected bill is already in the filtered results
        const selectedIndex = filteredItems.findIndex(
          (item) => (item.bill_id || item.zoho_billid || "") === selectedKey,
        );

        if (selectedIndex >= 0) {
          // Selected bill is in results, move it to top
          if (selectedIndex > 0) {
            const itemsCopy = [...filteredItems];
            const [selectedItem] = itemsCopy.splice(selectedIndex, 1);
            return {
              items: [selectedItem, ...itemsCopy],
              hasMore: result.hasMore,
            };
          }
          // Already at top, return as is
          return {
            items: filteredItems,
            hasMore: result.hasMore,
          };
        } else {
          // Selected bill not found in results
          // Check cache first
          const cachedBill = selectedBillCacheRef.current;
          if (cachedBill) {
            const cachedKey =
              cachedBill.bill_id || cachedBill.zoho_billid || "";
            if (cachedKey === selectedKey) {
              return {
                items: [cachedBill, ...filteredItems],
                hasMore: result.hasMore,
              };
            }
          }

          // Try to fetch bill details from Zoho API
          try {
            const billDetailsData = await fetchBillDetails(selectedBillId);
            // After fetching, create bill entity from the details
            if (billDetailsData) {
              const fetchedBill: VendorBillListItem = {
                bill_id: billDetailsData.bill_id || selectedBillId,
                vendor_id: billDetailsData.vendor_id || "",
                vendor_name: billDetailsData.vendor_name || "",
                bill_number: billDetailsData.bill_number || selectedBillNumber,
                date: billDetailsData.date || "",
                total: billDetailsData.total ?? 0,
                balance: billDetailsData.balance ?? 0,
                tds_total: billDetailsData.tds_total ?? 0,
                currency_code: billDetailsData.currency_code || "",
                zoho_billid: billDetailsData.bill_id || selectedBillId,
              };
              // Store in cache
              selectedBillCacheRef.current = fetchedBill;

              // ALSO set billDetails state so line items get processed automatically by useEffect
              setBillDetails(billDetailsData);

              return {
                items: [fetchedBill, ...filteredItems],
                hasMore: result.hasMore,
              };
            }
          } catch (error) {
            console.warn(
              "Could not fetch selected bill from Zoho API, using synthetic item:",
              error,
            );
          }

          // Fallback: Create synthetic bill
          const syntheticBill: VendorBillListItem = {
            bill_id: selectedBillId,
            vendor_id: "",
            vendor_name: "",
            bill_number: selectedBillNumber,
            total: 0,
            balance: 0,
            tds_total: 0,
            currency_code: "",
            zoho_billid: selectedBillId,
          };
          return {
            items: [syntheticBill, ...filteredItems],
            hasMore: result.hasMore,
          };
        }
      }

      return {
        items: filteredItems,
        hasMore: result.hasMore,
      };
    },
    [originalFetchVendorBills, fetchBillDetails],
  );

  // Zoho invoice details hook for fetching invoice line items

  // Clean Architecture: Use queries hook
  const {
    queries: queriesEntities,
    loading: queriesLoading,
    loadingMore: queriesLoadingMore,
    refetch: refetchQueries,
    hasMore: queriesHasMore,
    loadMore: loadMoreQueries,
  } = useCreditNoteQueries(documentId);

  // Currency state
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState<boolean>(false);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  // Error handling
  const { errors, setFieldErrors, setApiErrors, clearErrors, clearFieldError } =
    useErrorHandling();

  // UI State
  const [status, setStatus] = useState<
    "pending" | "approved" | "rejected" | "draft"
  >(isManualCreation ? "draft" : "pending");
  // For manual creation, always allow editing. For existing documents, check status.
  const [isReadOnly, setIsReadOnly] = useState<boolean>(
    isManualCreation ? false : false, // Start as false, will be set based on document status
  );

  const creditNoteStatusValue = useMemo<number | null>(() => {
    if (
      creditNoteDocument?.status === undefined ||
      creditNoteDocument?.status === null
    ) {
      return null;
    }
    return Number(creditNoteDocument.status);
  }, [creditNoteDocument]);

  const isUnappliedDraft = creditNoteStatusValue === 3;

  // Credit note type state for manual creation toggle
  const [creditNoteType, setCreditNoteType] = useState<"customer" | "vendor">(
    isManualCreation
      ? "customer"
      : creditNoteJson?.vendor_id && !creditNoteJson?.customer_id
        ? "vendor"
        : "customer",
  );

  // Detect if this is a vendor credit or customer credit
  const isVendorCreditNote = useMemo(() => {
    // For manual creation, use the toggle state
    if (isManualCreation) {
      return creditNoteType === "vendor";
    }
    // For existing documents, check the JSON
    return (
      creditNoteJson?.vendor_id !== undefined &&
      creditNoteJson?.vendor_id !== null &&
      creditNoteJson?.vendor_id !== "" &&
      (!creditNoteJson?.customer_id || creditNoteJson?.customer_id === "")
    );
  }, [creditNoteJson, creditNoteType, isManualCreation]);

  // Combined readonly state - disable only if readonly
  const isEditingDisabled = isReadOnly;

  // When posted, customer/vendor and all other fields should be disabled
  // Line items (quantity/rate) remain editable
  const isCustomerVendorDisabled = isEditingDisabled || isPostedToZoho;
  const isOtherFieldsDisabled = isEditingDisabled || isPostedToZoho;
  // Line items remain editable (don't include isPostedToZoho here)

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isDeleteApplicationConfirmOpen, setIsDeleteApplicationConfirmOpen] =
    useState(false);
  const [isDeleteApplicationLoading, setIsDeleteApplicationLoading] =
    useState(false);
  const [creditToDelete, setCreditToDelete] =
    useState<CreditAppliedEntity | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Display errors from hooks in ErrorBanner
  useEffect(() => {
    if (operationsError) {
      setApiErrors(new Error(operationsError));
    }
  }, [operationsError, setApiErrors]);

  // Clear errors when navigating to a different credit note
  useEffect(() => {
    clearErrors();
    clearOperationsError();
    clearInvoiceDetailsError();
    setBillDetailsError(null);
  }, [documentId, clearErrors, clearOperationsError, clearInvoiceDetailsError]);

  // Line item editing state
  const [editingLineItemIndex, setEditingLineItemIndex] = useState<
    number | null
  >(null);
  const [editingLineItemData, setEditingLineItemData] =
    useState<CreditNoteLineItemEntity | null>(null);
  const [originalAccountName, setOriginalAccountName] = useState<string>("");
  const [lineItems, setLineItems] = useState<CreditNoteLineItemEntity[]>(
    creditNoteJson?.line_items || [],
  );
  // Ref to track if we're updating lineItems ourselves (to prevent sync useEffect from overwriting)
  const isUpdatingLineItemsRef = useRef(false);
  const [isEditLineItemModalOpen, setIsEditLineItemModalOpen] =
    useState<boolean>(false);

  // Query and activity log state
  const [isQueryRaiseModalOpen, setIsQueryRaiseModalOpen] = useState(false);
  const [isQueryDetailModalOpen, setIsQueryDetailModalOpen] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | undefined>();
  const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);

  // Selected customer and invoice
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] =
    useState<string>("");

  // Selected vendor and bill (for vendor credits)
  const [selectedVendorId, setSelectedVendorId] = useState<string>("");
  const [selectedVendorName, setSelectedVendorName] = useState<string>("");
  const [selectedBillId, setSelectedBillId] = useState<string>("");
  const [selectedBillNumber, setSelectedBillNumber] = useState<string>("");

  // Store invoice/bill date for validation
  const [linkedDocumentDate, setLinkedDocumentDate] = useState<string>("");

  // TDS state for vendor credits
  const [isTDSApplicable, setIsTDSApplicable] = useState(false);

  // Ref for error banner to scroll to on validation errors
  const errorBannerRef = useRef<HTMLDivElement>(null);

  // Round amount to 2 decimal places
  const roundToTwoDecimals = useCallback((amount: number): number => {
    return Math.round(amount * 100) / 100;
  }, []);

  // const isCustomerCreditNote = useMemo(() => {
  //   return (
  //     creditNoteJson?.customer_id !== undefined &&
  //     creditNoteJson?.customer_id !== null &&
  //     creditNoteJson?.customer_id !== ""
  //   );
  // }, [creditNoteJson]);

  // Process invoice details when fetched and map to credit note line items
  useEffect(() => {
    // CRITICAL: NEVER import line items for posted documents - always use the JSON from credit note detail
    // For posted documents, line items are already in the database and should never be overwritten
    // This check MUST be at the very beginning, before any processing
    // Check both isPostedToZoho flag AND document status directly (defensive check)
    const isDocumentPosted =
      isPostedToZoho ||
      (creditNoteDocument?.status === "1" &&
        creditNoteDocument?.posted_to_zoho_date !== null &&
        creditNoteDocument?.posted_to_zoho_date !== undefined);

    if (isDocumentPosted) {
      // For posted documents, we never update line items from invoice details
      // Line items must always come from creditNoteJson (saved in database)
      return;
    }

    if (invoiceDetails && invoiceDetails.line_items) {
      // CRITICAL: Skip if line items have been manually edited (to prevent overwriting user edits)
      if (isUpdatingLineItemsRef.current) {
        return;
      }

      // Avoid overwriting saved drafts with fetched invoice lines
      // Check current creditNoteJson state (not just initialCreditNoteJson) to prevent
      // overwriting line items after applying approved changes
      const statusValue = Number(creditNoteDocument?.status);
      const isDraftStatus =
        statusValue === 0 || statusValue === 3 || statusValue === 4;
      const hasSavedLines =
        (creditNoteJson?.line_items?.length || 0) > 0 ||
        (initialCreditNoteJson?.line_items?.length || 0) > 0;
      const skipImportFromInvoice = hasSavedLines && isDraftStatus;
      if (skipImportFromInvoice) {
        return;
      }
      const zohoInvoiceId =
        invoiceDetails.invoice_id ||
        creditNoteJson?.zoho_invoiceid ||
        creditNoteJson?.linked_invoice_id ||
        "";

      // Map invoice line items to credit note line items
      // Exclude line_item_id as per requirements
      const mappedLineItems: CreditNoteLineItemEntity[] =
        invoiceDetails.line_items.map((item: any) => {
          // Calculate item_total and tax_amount manually for consistency (rounded to 2 decimals)
          const quantity = item.quantity || 0;
          const rate = item.rate || 0;
          const itemSubTotal = roundToTwoDecimals(quantity * rate);
          const taxPercentage = item.tax_percentage || 0;
          const taxAmount = roundToTwoDecimals(
            (itemSubTotal * taxPercentage) / 100,
          );

          const creditNoteLineItem: CreditNoteLineItemEntity = {
            description: item.description || item.name || "",
            quantity: quantity,
            rate: rate,
            account_id: item.account_id || "",
            account_name: item.account_name || "",
            hsn_or_sac: item.hsn_or_sac || "",
            tax_id: item.tax_id || "",
            tax_name: item.tax_name || "",
            tax_percentage: taxPercentage,
            tax_amount: taxAmount,
            item_total: itemSubTotal,
            invoice_id: zohoInvoiceId, // Link to invoice
            type: 1, // Credit note line item type
          };

          // Add TDS fields if present (for customer credits from invoice - usually not present)
          if (item.tds_tax_id) {
            creditNoteLineItem.tds_tax_id = item.tds_tax_id;
          }
          if (item.tds_tax_name) {
            creditNoteLineItem.tds_tax_name = item.tds_tax_name;
          }
          if (
            item.tds_tax_percentage !== undefined &&
            item.tds_tax_percentage !== null
          ) {
            creditNoteLineItem.tds_tax_percentage = item.tds_tax_percentage;
            // Calculate TDS amount based on itemSubTotal
            creditNoteLineItem.tds_tax_amount = roundToTwoDecimals(
              (itemSubTotal * item.tds_tax_percentage) / 100,
            );
          }

          // Explicitly exclude line_item_id - do not include it
          // Return only the fields we want

          return creditNoteLineItem;
        });

      // Update credit note JSON with mapped line items
      const invoiceId =
        invoiceDetails.invoice_id || creditNoteJson?.linked_invoice_id || "";
      const invoiceNumber =
        invoiceDetails.invoice_number || creditNoteJson?.reference_number || "";

      // ZOUR-65 — the note takes the PARENT's registration, here as well as at
      // the push.
      //
      // `NoteParentLocationResolver::enforce()` already overwrites this with the
      // parent's location server-side, so Zoho was always right; what was wrong
      // was the screen. Without this line the note's own `location_id` stayed
      // NULL, `LocationPicker` read NULL as the primary, and the page printed
      // "Head Office" directly above a caption promising it follows an invoice
      // raised in Bengaluru (UAT 3.5).
      //
      // NULL stays NULL: a parent with no registration of its own IS on the
      // primary, so the primary is then the true answer rather than a fallback.
      const parentLocationId = invoiceDetails.location_id ?? null;

      const updates: any = {
        linked_invoice_id: invoiceId,
        zoho_invoiceid: zohoInvoiceId,
        invoiceId: zohoInvoiceId,
        reference_number: invoiceNumber,
        location_id: parentLocationId,
        // CRITICAL: NEVER include line_items for posted documents
        // Even though we should have returned early, this is a defensive check
        ...(isDocumentPosted ? {} : { line_items: mappedLineItems }),
      };

      // Date field is now user-editable, no auto-population

      // Only update if there are actual changes to avoid infinite loops
      // CRITICAL: Exclude line_items comparison for posted documents
      const hasChanges =
        creditNoteJson?.linked_invoice_id !== invoiceId ||
        creditNoteJson?.zoho_invoiceid !== zohoInvoiceId ||
        creditNoteJson?.invoiceId !== zohoInvoiceId ||
        creditNoteJson?.reference_number !== invoiceNumber ||
        // Without this the fix would not fire on the common path: every other
        // field already matches by the time the parent's details land, so the
        // location alone has to be able to make this true.
        (creditNoteJson?.location_id ?? null) !== parentLocationId ||
        // Never compare line_items for posted documents
        (!isDocumentPosted &&
          JSON.stringify(creditNoteJson?.line_items) !==
            JSON.stringify(mappedLineItems));

      if (hasChanges) {
        updateCreditNoteJson(updates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    invoiceDetails,
    roundToTwoDecimals,
    isPostedToZoho,
    creditNoteDocument,
    // NOTE: creditNoteJson removed from deps to prevent overwriting manual edits
    // This useEffect should only run when invoiceDetails changes, not when creditNoteJson changes
  ]);

  // Initialize and sync local state from JSON
  useEffect(() => {
    if (creditNoteJson) {
      // Sync local state from JSON - Customer data
      if (creditNoteJson.customer_name) {
        setSelectedCustomerName(creditNoteJson.customer_name);
      }
      if (creditNoteJson.customer_id) {
        setSelectedCustomerId(creditNoteJson.customer_id);
      }
      if (creditNoteJson.linked_invoice_id || creditNoteJson.zoho_invoiceid) {
        // Pre-populate invoice dropdown if linked_invoice_id or zoho_invoiceid exists
        const invoiceId =
          creditNoteJson.linked_invoice_id ||
          creditNoteJson.zoho_invoiceid ||
          "";
        const invoiceNumber = creditNoteJson.reference_number || "";

        setSelectedInvoiceId(invoiceId);
        setSelectedInvoiceNumber(invoiceNumber);
      }

      // Sync local state from JSON - Vendor data (for existing documents only)
      if (!isManualCreation && documentId) {
        if (creditNoteJson.vendor_name) {
          setSelectedVendorName(creditNoteJson.vendor_name);
        }
        if (creditNoteJson.vendor_id) {
          setSelectedVendorId(creditNoteJson.vendor_id);
          // Update credit note type for existing documents
          setCreditNoteType("vendor");
        }
        if (creditNoteJson.linked_bill_id || creditNoteJson.zoho_billid) {
          // Pre-populate bill dropdown if linked_bill_id or zoho_billid exists
          const billId =
            creditNoteJson.linked_bill_id || creditNoteJson.zoho_billid || "";
          const billNumber = creditNoteJson.reference_number || "";

          setSelectedBillId(billId);
          setSelectedBillNumber(billNumber);
        }
        // Set type to customer if customer_id exists
        if (creditNoteJson.customer_id) {
          setCreditNoteType("customer");
        }
      }

      // Auto-populate country/state from JSON for existing documents
      // Note: For manual creation, these remain empty and user must select
      if (!isManualCreation && documentId) {
        // Country and state are already in creditNoteJson if they exist
        // They will be displayed in the dropdowns automatically
      }
    }
  }, [
    creditNoteJson,
    isManualCreation,
    initialCreditNoteJson,
    documentId,
    isUnappliedDraft,
  ]);

  // Fetch invoice/bill details on initial load if they exist
  // NOTE: For posted documents, we still fetch invoice/bill details for display (total/balance)
  // but we NEVER use them to update line items (handled by the invoiceDetails/billDetails useEffects)
  useEffect(() => {
    if (!creditNoteJson) return;
    // Fetch invoice details for customer credits
    if (
      !isVendorCreditNote &&
      (creditNoteJson.linked_invoice_id || creditNoteJson.zoho_invoiceid)
    ) {
      const invoiceId =
        creditNoteJson.linked_invoice_id || creditNoteJson.zoho_invoiceid;
      if (invoiceId && !invoiceDetails) {
        // Fetch invoice details even for posted documents (for display purposes)
        // The invoiceDetails useEffect will skip importing line items if isPostedToZoho is true
        fetchInvoiceDetails(invoiceId).catch((error) => {
          console.error("Error fetching invoice details on load:", error);
        });
      }
    }

    // Fetch bill details for vendor credits
    if (
      isVendorCreditNote &&
      (creditNoteJson.linked_bill_id || creditNoteJson.zoho_billid)
    ) {
      const billId =
        creditNoteJson.linked_bill_id || creditNoteJson.zoho_billid;
      if (billId && !billDetails) {
        setBillDetailsLoading(true);
        setBillDetailsError(null);
        fetchBillDetails(billId)
          .then((details) => {
            setBillDetails(details);
          })
          .catch((error) => {
            console.error("Error fetching bill details on load:", error);
            setBillDetailsError(
              error.message || "Failed to fetch bill details",
            );
          })
          .then(() => {
            setBillDetailsLoading(false);
          });
      }
    }
  }, [
    creditNoteJson,
    isVendorCreditNote,
    invoiceDetails,
    billDetails,
    fetchInvoiceDetails,
    fetchBillDetails,
    isUnappliedDraft,
  ]);

  // GST enabled state
  const [isGstEnabled, setIsGstEnabled] = useState(true);

  // ZOUR-19: does GST apply to this credit note?
  //
  // THE PARENT INVOICE'S DATE WINS. A note is not an independent document — it
  // adjusts one — so the question is whether GST applied to the supply being
  // corrected, not whether it applies today. A credit note raised now against
  // an invoice from before the client registered is reversing a supply that
  // carried no GST, and showing tax fields on it would invite someone to put a
  // tax credit into the books that was never charged.
  //
  // The note's own date is the fallback for a note whose parent has not been
  // picked yet, which is the normal state of a half-filled form. The server
  // resolves the same thing independently (GstNoteApplicability) and is what
  // actually decides the payload.
  //
  // THE SERVER'S ANSWER WINS (review 12). `creditNoteDocument.gst` comes from
  // the note GET, produced by the same GstNoteApplicability the push uses, and
  // it resolves the parent from the note's own `invoice_id` / `bill_id` rather
  // than from whatever the invoice picker happened to load. This page happened
  // to agree with the server in the chef's test, but only because its picker
  // had resolved — an unmapped note would have drawn tax the push then stripped,
  // exactly as the debit-note page did.
  //
  // The client-side fallback below covers a note opened from router state (no
  // GET, so no `gst` block), and is display-only either way.
  const serverGstDecision = creditNoteDocument?.gst;

  useEffect(() => {
    if (serverGstDecision) {
      setIsGstEnabled(serverGstDecision.applies);

      return;
    }

    // ZOUR-92: the fallback asks per registration too. The server block above
    // is the authority and is already location-aware; this only runs when the
    // note GET did not carry one, and it must not answer a different question
    // when it does run.
    setIsGstEnabled(
      gstAppliesToDocumentFor(
        invoiceDetails?.date || creditNoteJson?.date || "",
        creditNoteJson?.location_id ?? null
      )
    );
  }, [
    serverGstDecision,
    invoiceDetails?.date,
    creditNoteJson?.date,
    creditNoteJson?.location_id,
  ]);

  // ZOUR-19: the same question, as a value the JSX can read, so the page can
  // SAY why the tax fields are gone. The notice is derived from the date in
  // front of the user rather than from validation results, so it is right
  // immediately and right on a document that has never been validated.
  // ZOUR-65: the company's GST registrations.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
  } = useActiveLocations();

  /**
   * ZOUR-65 — does this note hang off a parent document?
   *
   * A note raised against an invoice or a bill INHERITS that document's GST
   * registration and the picker is read-only: Zoho does not enforce parent =
   * note (it accepts a mismatch silently and splits the tax the other way), so
   * the server overwrites it before pushing and the screen only reports it.
   *
   * A STANDALONE note has no parent to inherit from, and without a picker there
   * would be no way to file it under anything but the primary location — a
   * standalone credit note for a Bengaluru customer would silently land on Head
   * Office. So that one is editable.
   */
  const noteHasParent = useMemo(() => {
    const candidates = [
      creditNoteJson?.invoice_id,
      creditNoteJson?.bill_id,
      creditNoteJson?.linked_invoice_id,
      creditNoteJson?.zoho_invoiceid,
      ...(creditNoteJson?.line_items ?? []).map((line: any) => line?.invoice_id),
    ];

    return candidates.some(
      (value) => typeof value === "string" && value.trim() !== ""
    );
  }, [creditNoteJson]);

  const parentDocumentLabel = isVendorCreditNote ? "bill" : "invoice";

  const showPreGstNotice = serverGstDecision
    ? serverGstDecision.is_pre_registration
    : isPreGstRegistrationFor(
        invoiceDetails?.date || creditNoteJson?.date || "",
        creditNoteJson?.location_id ?? null
      );

  // Update lineItems when creditNoteJson changes (sync state with creditNoteJson)
  useEffect(() => {
    // CRITICAL: For posted documents, ALWAYS use line items from creditNoteJson (database)
    // Never allow invoice/bill details to overwrite line items for posted documents
    // This check MUST come FIRST, before any other checks
    if (isPostedToZoho) {
      if (creditNoteJson?.line_items && creditNoteJson.line_items.length > 0) {
        // For posted documents, always use line items from creditNoteJson (saved in database)
        // Never use invoice/bill line items for posted documents
        setLineItems(creditNoteJson.line_items);
      }
      return;
    }

    // Skip sync if we're the ones updating (to prevent overwriting our own changes)
    // This only applies to non-posted documents
    if (isUpdatingLineItemsRef.current) {
      isUpdatingLineItemsRef.current = false;
      return;
    }

    // Sync lineItems with creditNoteJson.line_items (for non-posted documents)
    if (creditNoteJson?.line_items !== undefined) {
      setLineItems(creditNoteJson.line_items);
    }
  }, [creditNoteJson?.line_items, isPostedToZoho]);

  // Load currencies
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
      } catch (err: any) {
        console.error("Error fetching currencies:", err);
        setCurrenciesError(err.message || "Failed to load currencies");
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  // Initial data loading effect
  useEffect(() => {
    const loadCreditNoteData = async () => {
      if (isManualCreation || !documentId) {
        setIsInitialLoading(false);
        // For manual creation, set draft status (which is under review)
        setStatus("draft");
        return;
      }

      try {
        setIsInitialLoading(true);
        setError(null);

        // Document is now fetched by the hook, but we may still need to fetch for credits applied
        // Fetch credit note document with status and zoho_id using repository (properly maps credits applied)
        const repository = new CreditNoteRepositoryImpl();
        const creditNote = await repository.getCreditNoteById(documentId);

        // Note: creditNoteDocument is now managed by the hook, but we fetch separately for credits applied

        // Extract credits applied if available
        if (creditNote.creditsApplied) {
          setCreditsApplied(creditNote.creditsApplied);
        } else {
          console.log("No credits applied found in credit note entity");
          console.log("Credit note keys:", Object.keys(creditNote));
          setCreditsApplied(null);
          setCreditsAppliedCalculations(null);
        }

        // Map status: 0 = draft/pending, 1 = completed/approved, 2 = under review, 3 = draft after unapply, 4 = draft
        // Draft (0, 3, or 4) and under review (2) should show as "pending"
        const statusValue = Number(creditNote.status);
        if (
          statusValue === 0 ||
          statusValue === 2 ||
          statusValue === 3 ||
          statusValue === 4
        ) {
          setStatus("pending"); // Draft and under review both show as pending
        } else if (statusValue === 1) {
          setStatus("approved");
        } else {
          setStatus("pending");
        }

        // Set read-only for rejected; otherwise allow editing (closed-month handling is separate)
        if (!isManualCreation) {
          setIsReadOnly(statusValue === 3);
        } else {
          setIsReadOnly(false);
        }

        // Load credit note JSON if available
        if (creditNote.json) {
          const jsonData =
            typeof creditNote.json === "string"
              ? JSON.parse(creditNote.json)
              : creditNote.json;
          setCreditNoteJson(jsonData);

          // CRITICAL: For posted documents, immediately set lineItems from database JSON
          // This ensures we use the saved line items from DB, not invoice/bill line items
          // This is especially important for posted documents where line items have been edited
          if (jsonData.line_items && jsonData.line_items.length > 0) {
            setLineItems(jsonData.line_items);
          }

          // Store original posted total for validation (if posted to Zoho)
          if (statusValue === 1 && creditNote.zoho_id && jsonData.total) {
            setOriginalPostedTotal(jsonData.total);
          }

          // Pre-populate invoice dropdown if linked_invoice_id or zoho_invoiceid exists
          if (jsonData.linked_invoice_id || jsonData.zoho_invoiceid) {
            const invoiceId =
              jsonData.linked_invoice_id || jsonData.zoho_invoiceid || "";
            const invoiceNumber = jsonData.reference_number || "";

            setSelectedInvoiceId(invoiceId);
            setSelectedInvoiceNumber(invoiceNumber);
          }
        }

        setIsInitialLoading(false);
      } catch (err: any) {
        console.error("Error loading credit note:", err);
        const errorMessage =
          err.message || "Failed to load credit note. Please try again.";
        setError(errorMessage);
        setIsInitialLoading(false);
        showError(errorMessage);
        setApiErrors(err);
      }
    };

    loadCreditNoteData();
  }, [
    documentId,
    isManualCreation,
    showError,
    setCreditNoteJson,
    setApiErrors,
    isEditMode,
  ]);

  // Helper function to find currency by ID
  const findCurrencyById = useCallback(
    (id: string) => {
      return currencies.find((currency) => currency.currency_id === id);
    },
    [currencies],
  );

  // Helper function to handle currency selection
  const handleCurrencyChange = useCallback(
    (currencyId: string) => {
      const selectedCurrency = findCurrencyById(currencyId);
      if (selectedCurrency) {
        updateCreditNoteJsonWithTracking({
          currency_id: selectedCurrency.currency_id,
          currency_code: selectedCurrency.currency_code,
          currency_symbol: selectedCurrency.currency_symbol,
        });
      }
    },
    [findCurrencyById, updateCreditNoteJsonWithTracking],
  );

  // Sync currency state from creditNoteJson - no effect needed, currency is in JSON

  // Calculate totals from line items and update JSON
  // Formula:
  // 1. Subtotal = sum of (qty * rate) for all line items
  // 2. Tax/GST = sum of tax_amount for all line items (calculated from tax_percentage if needed)
  // 3. Total = Subtotal + Tax
  useEffect(() => {
    if (!creditNoteJson) return;

    let calculatedSubTotal = 0;
    let calculatedTaxTotal = 0;

    lineItems.forEach((item) => {
      // 1. Calculate subtotal: qty * rate (excluding tax)
      const quantity = item.quantity || 0;
      const rate = item.rate || 0;
      const itemSubTotal = quantity * rate;
      calculatedSubTotal += itemSubTotal;

      // 2. Calculate tax amount for this line item
      // Use stored tax_amount if available, otherwise calculate from tax_percentage
      let itemTaxAmount = 0;
      if (item.tax_amount !== undefined && item.tax_amount !== null) {
        itemTaxAmount = item.tax_amount;
      } else if (item.tax_percentage) {
        itemTaxAmount = (itemSubTotal * item.tax_percentage) / 100;
      }

      calculatedTaxTotal += itemTaxAmount;
    });

    // Round all totals to 2 decimal places
    calculatedSubTotal = roundToTwoDecimals(calculatedSubTotal);
    calculatedTaxTotal = roundToTwoDecimals(calculatedTaxTotal);

    // 3. Calculate total: subtotal + tax (already rounded values)
    const calculatedTotal = roundToTwoDecimals(
      calculatedSubTotal + calculatedTaxTotal,
    );

    // Update JSON if totals changed
    if (
      creditNoteJson.sub_total !== calculatedSubTotal ||
      creditNoteJson.tax_total !== calculatedTaxTotal ||
      creditNoteJson.tax_amount !== calculatedTaxTotal ||
      creditNoteJson.total !== calculatedTotal
    ) {
      updateCreditNoteJson({
        sub_total: calculatedSubTotal,
        tax_total: calculatedTaxTotal,
        tax_amount: calculatedTaxTotal,
        total: calculatedTotal,
      });
    }
  }, [lineItems, creditNoteJson, updateCreditNoteJson, roundToTwoDecimals]);

  // Validate total amount against invoice/bill amount whenever it changes
  // useEffect(() => {
  //   if (!creditNoteJson?.total) {
  //     clearFieldError("total");
  //     return;
  //   }

  //   const currentTotal = creditNoteJson.total;

  //   // Helper to format amount inline
  //   const formatAmountInline = (amount: number) =>
  //     amount.toLocaleString("en-IN", {
  //       minimumFractionDigits: 2,
  //       maximumFractionDigits: 2,
  //     });

  //   // Check if total exceeds invoice/bill amount
  //   if (isVendorCreditNote) {
  //     const billTotal = billDetails?.total;
  //     if (billTotal && currentTotal > billTotal) {
  //       setFieldErrors({
  //         total: `Total amount (${creditNoteJson?.currency_symbol || "₹"}${formatAmountInline(currentTotal)}) cannot exceed the bill amount (${
  //           creditNoteJson?.currency_symbol || "₹"
  //         }${formatAmountInline(billTotal)})`,
  //       });
  //       return;
  //     }
  //   } else {
  //     const invoiceTotal = invoiceDetails?.total;
  //     if (invoiceTotal && currentTotal > invoiceTotal) {
  //       setFieldErrors({
  //         total: `Total amount (${creditNoteJson?.currency_symbol || "₹"}${formatAmountInline(currentTotal)}) cannot exceed the invoice amount (${
  //           creditNoteJson?.currency_symbol || "₹"
  //         }${formatAmountInline(invoiceTotal)})`,
  //       });
  //       return;
  //     }
  //   }

  //   // Check minimum for posted credit notes
  //   if (
  //     isPostedToZoho &&
  //     originalPostedTotal !== null &&
  //     currentTotal < originalPostedTotal
  //   ) {
  //     setFieldErrors({
  //       total: `Total amount cannot be less than the originally posted amount (${
  //         creditNoteJson?.currency_symbol || "₹"
  //       }${formatAmountInline(originalPostedTotal)})`,
  //     });
  //     return;
  //   }

  //   // Clear total error if all checks pass
  //   clearFieldError("total");
  // }, [
  //   creditNoteJson?.total,
  //   creditNoteJson?.currency_symbol,
  //   isVendorCreditNote,
  //   billDetails?.total,
  //   invoiceDetails?.total,
  //   isPostedToZoho,
  //   originalPostedTotal,
  //   setFieldErrors,
  //   clearFieldError,
  // ]);

  // Validation function
  const validateCreditNote = useCallback((): boolean => {
    // Clear all previous validation errors first
    clearErrors();

    const newErrors: Record<string, string> = {};

    // Required field validations
    // Note: reference_number is auto-populated from linked invoice/bill
    if (!creditNoteJson?.creditnote_number?.trim()) {
      newErrors.creditnote_number = "Credit note number is required";
    }
    if (!creditNoteJson?.date?.trim()) {
      newErrors.date = "Date is required";
    } else if (linkedDocumentDate) {
      // Validate that credit note date is not before invoice/bill date
      const creditNoteDate = new Date(creditNoteJson.date);
      const documentDate = new Date(linkedDocumentDate);
      if (creditNoteDate < documentDate) {
        newErrors.date = `Credit note date cannot be before the ${
          isVendorCreditNote ? "bill" : "invoice"
        } date (${linkedDocumentDate})`;
      }
    }
    if (!creditNoteJson?.currency_id) {
      newErrors.currency_id = "Currency is required";
    }
    if (!creditNoteJson?.country?.trim()) {
      newErrors.country = "Country is required";
    }
    // State is required only if country is India
    if (creditNoteJson?.country === "IN" && !creditNoteJson?.state?.trim()) {
      newErrors.state = "State is required when country is India";
    }
    if (lineItems.length === 0) {
      newErrors.line_items = "At least one line item is required";
    }

    // Validate based on credit note type (customer vs vendor)
    if (isVendorCreditNote) {
      // Vendor credit validations
      if (!creditNoteJson?.vendor_id) {
        newErrors.vendor_id = "Vendor is required";
      }
      // Linked Bill is mandatory for vendor credits
      if (!creditNoteJson?.linked_bill_id?.trim()) {
        newErrors.linked_bill_id = "Linked bill is required";
      }
      // GSTIN is required only if vendor is selected AND has business_gst treatment
      if (
        creditNoteJson?.vendor_id &&
        creditNoteJson?.gst_treatment === "business_gst" &&
        !creditNoteJson?.gst_no?.trim()
      ) {
        newErrors.gst_no = "GSTIN is required for Business GST";
      }
    } else {
      // Customer credit validations
      if (!creditNoteJson?.customer_id) {
        newErrors.customer_id = "Customer is required";
      }
      // Linked Invoice is mandatory for customer credits
      if (!creditNoteJson?.linked_invoice_id?.trim()) {
        newErrors.linked_invoice_id = "Linked invoice is required";
      }
      // GSTIN is required only if customer is selected AND has business_gst treatment
      if (
        creditNoteJson?.customer_id &&
        creditNoteJson?.gst_treatment === "business_gst" &&
        !creditNoteJson?.gst_no?.trim()
      ) {
        newErrors.gst_no = "GSTIN is required for Business GST";
      }
    }

    // Validate line items
    lineItems.forEach((item, index) => {
      if (!item.description?.trim()) {
        newErrors[`line_item_${index}_description`] = `Line item ${
          index + 1
        }: Description is required`;
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`line_item_${index}_quantity`] = `Line item ${
          index + 1
        }: Quantity must be greater than 0`;
      }
      if (!item.rate || item.rate <= 0) {
        newErrors[`line_item_${index}_rate`] = `Line item ${
          index + 1
        }: Rate must be greater than 0`;
      }
    });

    // Helper to format amount inline
    const formatAmountInline = (amount: number) =>
      amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    // Validation for Posted to Zoho credit notes - amount constraints (minimum)
    // REMOVED: Allow users to enter any amount when updating posted credit notes
    // The backend will handle the delete and reapply logic

    // Validation for ALL credit notes - total cannot exceed invoice/bill balance OR total (depending on context)
    const currentTotal = creditNoteJson?.total || 0;
    console.log("Validating total amount:", {
      currentTotal,
      isVendorCreditNote,
      isPostedToZoho,
      billBalance: billDetails?.balance,
      billTotal: billDetails?.total,
      invoiceBalance: invoiceDetails?.balance,
      invoiceTotal: invoiceDetails?.total,
    });

    // Validation for credits applied (when credit note is linked to invoice/bill with other credits)
    if (creditsApplied && creditsApplied.credits.length > 0) {
      const currentZohoId = creditNoteDocument?.zoho_id;
      // Get invoice/bill total from invoice/bill details
      const invoiceBillTotal = isVendorCreditNote
        ? (billDetails?.total ?? 0)
        : (invoiceDetails?.total ?? 0);

      // Calculate total of other credits (excluding current one if it has zoho_id)
      // If current credit note doesn't have zoho_id yet, include all credits in the calculation
      const otherCreditsTotal = creditsApplied.credits
        .filter((credit) => {
          // Only exclude current credit if it has a zoho_id and matches
          if (currentZohoId) {
            if (creditsApplied.isVendorCredit) {
              return credit.vendor_credit_id !== currentZohoId;
            } else {
              return credit.creditnote_id !== currentZohoId;
            }
          }
          // If no zoho_id, include all credits (current credit note isn't posted yet)
          return true;
        })
        .reduce((sum, credit) => {
          const amount = credit.amount_applied || credit.amount || 0;
          return sum + amount;
        }, 0);

      // Calculate max allowed amount
      // If current credit note has zoho_id, exclude it from other credits
      // If not, the current credit note amount should be included in validation
      const maxAllowedAmount = invoiceBillTotal - otherCreditsTotal;

      if (currentTotal > maxAllowedAmount) {
        newErrors.total = `Amount cannot exceed ${
          creditNoteJson?.currency_symbol || "₹"
        }${formatAmountInline(maxAllowedAmount)}. Total credits applied ${
          creditNoteJson?.currency_symbol || "₹"
        }${formatAmountInline(otherCreditsTotal)} + ${
          creditNoteJson?.currency_symbol || "₹"
        }${formatAmountInline(currentTotal)} would exceed ${
          isVendorCreditNote ? "bill" : "invoice"
        } total ${creditNoteJson?.currency_symbol || "₹"}${formatAmountInline(
          invoiceBillTotal,
        )}. Please update other credits or reduce this amount.`;
      }
    } else if (isVendorCreditNote) {
      // For posted credit notes, validate against total (differential logic)
      // For new credit notes, validate against balance
      const billLimit = isPostedToZoho
        ? billDetails?.total
        : billDetails?.balance;
      const limitLabel = isPostedToZoho ? "bill total" : "bill balance";

      if (billLimit !== undefined && currentTotal > billLimit) {
        console.log(`VALIDATION ERROR: Total exceeds ${limitLabel}`, {
          currentTotal,
          billLimit,
        });
        newErrors.total = `Total amount (${
          creditNoteJson?.currency_symbol || "₹"
        }${formatAmountInline(
          currentTotal,
        )}) cannot exceed the ${limitLabel} (${
          creditNoteJson?.currency_symbol || "₹"
        }${formatAmountInline(billLimit)})`;
      }
    } else {
      // For posted credit notes, validate against total (differential logic)
      // For new credit notes, validate against balance
      const invoiceLimit = isPostedToZoho
        ? invoiceDetails?.total
        : invoiceDetails?.balance;
      const limitLabel = isPostedToZoho ? "invoice total" : "invoice balance";

      console.log("invoiceLimit", invoiceLimit);
      console.log("currentTotal", currentTotal);
      if (invoiceLimit !== undefined && currentTotal > invoiceLimit) {
        console.log(`VALIDATION ERROR: Total exceeds ${limitLabel}`, {
          currentTotal,
          invoiceLimit,
        });
        newErrors.total = `Total amount (${
          creditNoteJson?.currency_symbol || "₹"
        }${formatAmountInline(
          currentTotal,
        )}) cannot exceed the ${limitLabel} (${
          creditNoteJson?.currency_symbol || "₹"
        }${formatAmountInline(invoiceLimit)})`;
      }
    }

    // Set all validation errors at once (after clearing)
    if (Object.keys(newErrors).length > 0) {
      console.log("Validation errors found:", newErrors);
      setFieldErrors(newErrors);
    } else {
      console.log("No validation errors - all checks passed");
    }

    return Object.keys(newErrors).length === 0;
  }, [
    creditNoteJson,
    lineItems,
    linkedDocumentDate,
    clearErrors,
    setFieldErrors,
    isPostedToZoho,
    billDetails?.balance,
    billDetails?.total,
    invoiceDetails?.balance,
    invoiceDetails?.total,
    creditsApplied,
    creditNoteDocument?.zoho_id,
    isVendorCreditNote,
  ]);

  const confirmDeleteCreditApplied = useCallback(
    (credit: CreditAppliedEntity) => {
      setCreditToDelete(credit);
      setIsDeleteApplicationConfirmOpen(true);
    },
    [],
  );

  const handleConfirmDeleteCreditNoteApplication = useCallback(
    async (creditNoteId: string | number, credit: CreditAppliedEntity) => {
      try {
        setIsDeleteApplicationLoading(true);
        const result = await deleteCreditNote(creditNoteId);
        if (result) {
          // Remove locally from creditsApplied list
          setCreditsApplied((prev) => {
            if (!prev) return prev;
            const filtered = prev.credits.filter((c) => {
              if (prev.isVendorCredit) {
                return c.vendor_credit_id !== credit.vendor_credit_id;
              }
              return c.creditnote_id !== credit.creditnote_id;
            });
            return {
              ...prev,
              credits: filtered,
            };
          });
          // Clear derived calculations if list emptied
          setCreditsAppliedCalculations((prevCalc) => {
            const hasCredits =
              creditsApplied &&
              creditsApplied.credits.some((c) =>
                creditsApplied.isVendorCredit
                  ? c.vendor_credit_id !== credit.vendor_credit_id
                  : c.creditnote_id !== credit.creditnote_id,
              );
            if (hasCredits) {
              return prevCalc;
            }
            return null;
          });
        }
        setIsDeleteApplicationConfirmOpen(false);
        setCreditToDelete(null);
      } finally {
        setIsDeleteApplicationLoading(false);
      }
    },
    [deleteCreditNote, creditsApplied],
  );

  // Handler for navigating to another credit note
  const handleNavigateToCreditNote = useCallback(
    (credit: CreditAppliedEntity) => {
      // Clear all errors before navigating to prevent them from showing on the next page
      clearErrors();
      clearOperationsError();
      clearInvoiceDetailsError();
      setBillDetailsError(null);

      const documentId = credit.creditnote_id;
      navigate(`/credit/review/${documentId}`, {
        state: {
          isEdit: true,
          documentId: documentId,
        },
      });
    },
    [navigate, clearErrors, clearOperationsError, clearInvoiceDetailsError],
  );

  // Extract credits applied from invoice/bill details when creating new credit note
  useEffect(() => {
    // Only extract credits applied if we're creating a new credit note (no existing creditsApplied)
    // and we have invoice/bill details with credits_applied
    if (!creditsApplied && (invoiceDetails || billDetails)) {
      const creditsFromDetails =
        invoiceDetails?.credits_applied ||
        (billDetails as any)?.credits_applied;

      if (
        creditsFromDetails &&
        creditsFromDetails.credits &&
        creditsFromDetails.credits.length > 0
      ) {
        setCreditsApplied(creditsFromDetails);
      }
    }
  }, [invoiceDetails, billDetails, creditsApplied]);

  // Calculate credits applied values after API response
  useEffect(() => {
    if (creditsApplied && creditsApplied.credits.length > 0) {
      const invoiceBillTotal = creditsApplied.isVendorCredit
        ? (billDetails?.total ?? 0)
        : (invoiceDetails?.total ?? 0);

      const totalCreditsApplied = creditsApplied.credits.reduce(
        (sum, credit) => sum + (credit.amount_applied || credit.amount || 0),
        0,
      );

      const remainingBalance = invoiceBillTotal - totalCreditsApplied;

      setCreditsAppliedCalculations({
        invoiceBillTotal,
        totalCreditsApplied,
        remainingBalance,
      });
    } else {
      setCreditsAppliedCalculations(null);
    }
  }, [creditsApplied, invoiceDetails?.total, billDetails?.total]);

  // Handler for deleting credit note
  const handleDeleteCreditNote = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDeleteCreditNote = useCallback(async () => {
    if (!documentId) {
      showError("No document ID available");
      return;
    }

    // Closed month check first: non-Admin/CFO must submit delete for approval
    if (isMonthClosed && !isAdminOrCFO && isPostedToZoho) {
      setIsDeleteConfirmOpen(false);
      setPendingClosedMonthSource("delete");
      setShowClosedMonthConfirmModal(true);
      return;
    }

    // Posted to Zoho: require delete_after_push permission
    if (isPostedToZoho && !canDeleteAfterPush) {
      showError(
        getDeleteAfterPushPermissionDeniedMessage("credit notes", accountingBackend),
      );
      return;
    }

    // Posted and permission exists but action requires review → open review modal (Delete After Pushed To Zoho)
    if (isPostedToZoho && canDeleteAfterPush && needsReviewForDeleteAfterPush) {
      const changeDetails: ChangeDetails = {
        action_types: ["delete_after_push_to_zoho"],
        raw_json: omitSupplyFields(
          creditNoteJson ?? originalCreditNoteJson ?? undefined,
        ),
        original_json: originalCreditNoteJson ?? undefined,
        field_changes: [],
      };
      setPendingReviewAction({
        changeDetails,
        disableSubmit: false,
        actionCode: ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO,
      });
      setIsReviewModalOpen(true);
      setIsDeleteConfirmOpen(false);
      return;
    }

    try {
      setIsDeleteLoading(true);
      const success = await deleteCreditNote(documentId);
      if (success) {
        showSuccess("Credit note deleted successfully");
        setIsDeleteConfirmOpen(false);
        // Navigate back to credit notes section
        navigate("/dashboard/credit");
      } else {
        showError(operationsError || "Failed to delete credit note");
      }
    } catch (err: any) {
      showError(err.message || "Failed to delete credit note");
    } finally {
      setIsDeleteLoading(false);
    }
  }, [
    documentId,
    navigate,
    showError,
    showSuccess,
    deleteCreditNote,
    operationsError,
    isMonthClosed,
    isAdminOrCFO,
    isPostedToZoho,
    canDeleteAfterPush,
    needsReviewForDeleteAfterPush,
    creditNoteJson,
    originalCreditNoteJson,
  ]);

  // Handler functions

  const handleReject = useCallback(async () => {
    if (!documentId) {
      showError("No document ID available");
      return;
    }

    try {
      clearOperationsError();
      const success = await rejectCreditNote(parseInt(documentId));
      if (success) {
        setStatus("rejected");
        showSuccess("Credit note rejected successfully");
        navigate("/dashboard/credit");
      } else {
        showError(operationsError || "Failed to reject credit note");
      }
    } catch (err: any) {
      showError(err.message || "Failed to reject credit note");
    }
  }, [
    documentId,
    rejectCreditNote,
    showSuccess,
    showError,
    navigate,
    clearOperationsError,
    operationsError,
  ]);

  const handleSaveAsDraft = useCallback(async () => {
    // Check permission for create action
    if (!canCreate) {
      showError("You do not have permission to create credit notes");
      return;
    }

    // Run validation first
    if (!validateCreditNote()) {
      console.log("Validation failed - blocking save as draft");
      // ErrorBanner will display all validation errors
      // Scroll to error banner
      if (errorBannerRef.current) {
        errorBannerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      return;
    }

    console.log("Validation passed - proceeding with save as draft");

    if (!creditNoteJson) {
      showError("No credit note data available");
      return;
    }

    // Check if review is needed for create action
    if (needsReviewForCreate) {
      // Build changeDetails for review
      const changeDetails: ChangeDetails = {
        reason: "Requesting permission to create credit note",
        field_changes: trackedChanges.length > 0 ? trackedChanges : [],
        raw_json: omitSupplyFields(creditNoteJsonRef.current || creditNoteJson),
        action_types: ["create"],
      };

      setPendingReviewAction({
        changeDetails,
        disableSubmit: trackedChanges.length === 0,
        actionCode: ACTION_CODES.CREATE,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // No review needed, proceed directly
    try {
      clearOperationsError();
      // For new credit notes (manual creation), create a draft using saveCreditNoteAsDraft
      if (!documentId || isManualCreation) {
        // Ensure customer_name or vendor_name is included in the JSON
        const draftData = omitSupplyFields({
          ...creditNoteJson,
          ...(isVendorCreditNote && selectedVendorName
            ? { vendor_name: selectedVendorName }
            : {}),
          ...(!isVendorCreditNote && selectedCustomerName
            ? { customer_name: selectedCustomerName }
            : {}),
        });

        // Create new draft credit note
        const savedCreditNote = await saveCreditNoteAsDraft(draftData);

        if (savedCreditNote) {
          // Navigate to the saved credit note so auto-save can take over
          navigate("/dashboard/credit");
          setStatus("draft"); // UI shows as "pending" (under review)
          showSuccess("Credit note saved as draft successfully");
        } else {
          showError(operationsError || "Failed to save credit note as draft");
        }
      } else {
        // For existing documents, auto-save already handles updates
        // Just show success message
        showSuccess("Credit note saved as draft successfully");
      }
    } catch (err: any) {
      showError(err.message || "Failed to save credit note as draft");
      setApiErrors(err);
    }
  }, [
    canCreate,
    needsReviewForCreate,
    validateCreditNote,
    creditNoteJson,
    trackedChanges,
    creditNoteJsonRef,
    documentId,
    isManualCreation,
    isVendorCreditNote,
    selectedCustomerName,
    selectedVendorName,
    saveCreditNoteAsDraft,
    showSuccess,
    showError,
    navigate,
    clearOperationsError,
    operationsError,
    setApiErrors,
  ]);

  // Handle review modal success
  const handleReviewModalSuccess = async (comments?: string) => {
    try {
      // If document is NOT posted to Zoho, we need to save as draft first
      if (!isPostedToZoho && !documentId) {
        // Ensure customer_name or vendor_name is included in the JSON
        const draftData = omitSupplyFields({
          ...creditNoteJson,
          ...(isVendorCreditNote && selectedVendorName
            ? { vendor_name: selectedVendorName }
            : {}),
          ...(!isVendorCreditNote && selectedCustomerName
            ? { customer_name: selectedCustomerName }
            : {}),
        });

        // Save as draft first to get the ID
        const savedCreditNote = await saveCreditNoteAsDraft(draftData);

        if (!savedCreditNote || !savedCreditNote.id) {
          showError(operationsError || "Failed to save credit note as draft");
          return;
        }

        const creditNoteId = savedCreditNote.id;

        // Now send the review request with the actual credit note ID
        const { createReviewRequest } =
          await import("../../../../shared/utils/reviewWorkflow");
        await createReviewRequest({
          module_code: MODULE_CODES.CREDIT_NOTE,
          action_code:
            pendingReviewAction?.actionCode || ACTION_CODES.PUSH_TO_ZOHO,
          entity_type: "credit_note",
          entity_id: creditNoteId,
          request_comments: comments || "Please review this credit note",
          change_details: pendingReviewAction?.changeDetails || undefined,
        });

        showSuccess("Credit note saved as draft and review requested");
      } else {
        // Document is already posted or has an ID; ReviewRequestModal already showed success toast
      }

      // Close the modal
      setIsReviewModalOpen(false);

      // Clear tracked changes after submitting review
      setTrackedChanges([]);

      // Update original document to current state
      if (creditNoteJson) {
        setOriginalCreditNoteJson(creditNoteJson);
      }

      // Navigate back to credit notes list
      navigate("/dashboard/credit");
    } catch (error: any) {
      console.error("Error handling review modal success:", error);
      showError(error.message || "Failed to process review request");
      // Keep modal open so user can see the error and try again
    }
  };

  const handleApproveAndPost = useCallback(async () => {
    // Run validation first
    if (!validateCreditNote()) {
      // ErrorBanner will display all validation errors
      // Scroll to error banner
      if (errorBannerRef.current) {
        errorBannerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      return;
    }

    // Determine which action we're performing
    const isUpdateAfterPush = isPostedToZoho || isEditMode;
    const actionCode = isUpdateAfterPush
      ? ACTION_CODES.UPDATE_AFTER_PUSH
      : ACTION_CODES.PUSH_TO_ZOHO;
    const needsReview = isUpdateAfterPush
      ? needsReviewForUpdateAfterPush
      : needsReviewForPushToZoho;
    const hasPermission = isUpdateAfterPush
      ? canUpdateAfterPush
      : canPushToZoho;

    // Check permission
    if (!hasPermission) {
      const errorMessage = isUpdateAfterPush
        ? getUpdateAfterPostPermissionDeniedMessage("credit notes", accountingBackend)
        : getPushPermissionDeniedMessage("credit notes", accountingBackend);
      showError(errorMessage);
      return;
    }

    // Closed month: non-Admin/CFO cannot approve/update to Zoho directly; show confirm modal to submit for review
    if (isMonthClosed && !isAdminOrCFO) {
      const fieldChanges =
        trackedChanges.length > 0
          ? trackedChanges
          : getFieldChangesFromCreditNoteForm(creditNoteJson);
      setPendingReviewAction({
        changeDetails: {
          closed_month: true,
          action_types: [
            isUpdateAfterPush ? "update_after_push" : "push_to_zoho",
            ACTION_TYPE_MONTH_CLOSE,
          ],
          field_changes: fieldChanges,
          raw_json: omitSupplyFields(creditNoteJson ?? undefined),
          original_json: originalCreditNoteJson ?? undefined,
        },
        disableSubmit: false,
        actionCode,
      });
      setPendingClosedMonthSource("approve");
      setShowClosedMonthConfirmModal(true);
      return;
    }

    // Build the actual action function
    const executeAction = async () => {
      try {
        clearOperationsError();

        // If in edit mode (posted to zoho), always use updateZohoCreditNote
        if (isEditMode && documentId) {
          const success = await updateZohoCreditNote(
            documentId,
            omitSupplyFields(creditNoteJson),
          );

          if (success) {
            showSuccess("Credit note updated successfully");
            navigate("/dashboard/credit");
          } else {
            showError(operationsError || "Failed to update credit note");
          }
          return;
        }

        const documentStatus = creditNoteDocument?.status
          ? Number(creditNoteDocument.status)
          : null;
        const hasZohoId = creditNoteDocument?.zoho_id;

        // Logic based on zoho_id and status:
        // 1. If zoho_id exists and status is 1 (completed/approved) -> use updateZohoCreditNote
        // 2. If zoho_id exists and status is 0 or 2 (draft/under review) -> use approveCreditNote
        // 3. If no zoho_id -> use createZohoCreditNote (pushToZoho) for new credit notes
        //    OR use update endpoint for existing credit notes that need to be pushed

        // Ensure customer_name or vendor_name is included in the JSON
        const creditNoteData = omitSupplyFields({
          ...creditNoteJson,
          ...(isVendorCreditNote && selectedVendorName
            ? { vendor_name: selectedVendorName }
            : {}),
          ...(!isVendorCreditNote && selectedCustomerName
            ? { customer_name: selectedCustomerName }
            : {}),
        });

        let success = false;

        if (hasZohoId && documentStatus === 1) {
          // Already posted to Zoho and completed - update it
          success = await updateZohoCreditNote(
            parseInt(documentId),
            creditNoteData,
          );
        } else if (
          hasZohoId &&
          (documentStatus === 0 || documentStatus === 2 || documentStatus === 3)
        ) {
          // Has zoho_id but status is draft/under review - approve it
          success = await createZohoCreditNote(
            creditNoteData,
            signedUrl || undefined,
            documentId ? parseInt(documentId) : undefined,
          );
        } else if (!documentId || isManualCreation) {
          // No documentId or manual creation - create new credit note and push to Zoho
          success = await createZohoCreditNote(
            creditNoteData,
            signedUrl || undefined,
          );
        } else {
          // Existing credit note without zoho_id - pass documentId to update existing draft
          success = await createZohoCreditNote(
            creditNoteData,
            signedUrl || undefined,
            parseInt(documentId),
          );
        }

        if (success) {
          setStatus("approved");
          const successMessage = isVendorCreditNote
            ? "Vendor credit approved and posted successfully"
            : "Credit note approved and posted successfully";
          showSuccess(successMessage);
          navigate("/dashboard/credit");
        } else {
          const errorMessage = isVendorCreditNote
            ? operationsError || "Failed to approve and post vendor credit"
            : operationsError || "Failed to approve and post credit note";
          showError(errorMessage);
        }
      } catch (err: any) {
        showError(err.message || "Failed to approve and post credit note");
        setApiErrors(err);
      }
    };

    // Check if review is needed
    if (needsReview) {
      // Reconstruct original credit note data from already-loaded data
      // Use the already-loaded creditNoteDocument and creditsApplied state
      // instead of making another API call
      let originalCreditNoteData:
        | { credit_note: any; credits_applied: any }
        | undefined = undefined;

      if (documentId && creditNoteDocument) {
        // Reconstruct the API response structure from already-loaded data
        originalCreditNoteData = {
          credit_note: creditNoteDocument,
          credits_applied: creditsApplied || null,
        };
      }

      // Check if there are tracked changes
      if (trackedChanges.length === 0) {
        // No changes - show modal but disable submit
        const changeDetails: ChangeDetails = {
          reason:
            "No changes detected. Please make changes to the document before requesting a review.",
          field_changes: [],
          raw_json: omitSupplyFields(creditNoteJsonRef.current || creditNoteJson),
          action_types: [actionCode],
          ...(originalCreditNoteData && {
            original_credit_note: originalCreditNoteData,
          }),
        };

        setPendingReviewAction({
          // action: executeAction,
          changeDetails,
          disableSubmit: true,
          actionCode,
        });
        setIsReviewModalOpen(true);
        return;
      }

      // There are changes - show review modal with changes
      const changeDetails: ChangeDetails = {
        reason: isUpdateAfterPush
          ? getReviewRequestUpdateAfterPostReason("credit note", accountingBackend)
          : getReviewRequestPushReason("credit note", accountingBackend),
        field_changes: trackedChanges.length > 0 ? trackedChanges : [],
        raw_json: omitSupplyFields(creditNoteJsonRef.current || creditNoteJson),
        action_types: [actionCode],
        ...(originalCreditNoteData && {
          original_credit_note: originalCreditNoteData,
        }),
      };

      setPendingReviewAction({
        // action: executeAction,
        changeDetails,
        disableSubmit: false,
        actionCode,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // No review needed, proceed directly
    await executeAction();
  }, [
    creditNoteJson,
    isMonthClosed,
    isAdminOrCFO,
    documentId,
    isVendorCreditNote,
    selectedCustomerName,
    selectedVendorName,
    validateCreditNote,
    createZohoCreditNote,
    updateZohoCreditNote,
    creditNoteDocument,
    signedUrl,
    showSuccess,
    showError,
    navigate,
    clearOperationsError,
    operationsError,
    setApiErrors,
    isManualCreation,
    isEditMode,
    isPostedToZoho,
    canUpdateAfterPush,
    canPushToZoho,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    trackedChanges,
    getFieldChangesFromCreditNoteForm,
    creditNoteJsonRef,
    creditsApplied,
  ]);

  // Customer selection handler
  const handleCustomerSelect = useCallback(
    (customer: any) => {
      // Prevent selection if customer/vendor fields are disabled (e.g., when posted)
      if (isCustomerVendorDisabled) {
        return;
      }

      // CRITICAL: Clear all invoice-related state when customer changes
      // Clear invoice selection
      setSelectedInvoiceId("");
      setSelectedInvoiceNumber("");
      // Clear invoice details
      resetInvoiceDetails();
      // Clear line items
      setLineItems([]);
      updateCreditNoteJson({
        linked_invoice_id: undefined,
        zoho_invoiceid: undefined,
        invoiceId: undefined,
        reference_number: "",
        line_items: [],
      });
      // Clear credits applied
      setCreditsApplied(null);
      setCreditsAppliedCalculations(null);
      // Clear linked document date
      setLinkedDocumentDate("");

      // Cache the selected customer
      selectedCustomerCacheRef.current = customer;

      setSelectedCustomerId(customer.zoho_customerid || "");
      setSelectedCustomerName(
        customer.customer_name || customer.company_name || "",
      );

      const customerCountry = customer.country || "";
      const customerState =
        customerCountry === "IN" ? customer.state || "" : "";

      const updates: Partial<CreditNoteDataEntity> = {
        customer_id: customer.zoho_customerid || "",
        customer_name: customer.customer_name || customer.company_name || "",
        gst_treatment: (customer.gst_treatment || "") as any,
        gst_no: customer.gstin_vat_tax_id || "",
        country: customerCountry,
        state: customerState,
      };

      // Auto-populate currency if customer has one.
      if (customer.currency_id) {
        const customerCurrency = findCurrencyById(customer.currency_id);
        if (customerCurrency) {
          handleCurrencyChange(customer.currency_id);
        }
      }

      // Use updateCreditNoteJsonWithTracking to track customer changes
      updateCreditNoteJsonWithTracking(updates);

      // Clear customer error when customer is selected
      clearFieldError("customer_id");
      clearFieldError("country");
      clearFieldError("state");
    },
    [
      updateCreditNoteJsonWithTracking,
      findCurrencyById,
      handleCurrencyChange,
      clearFieldError,
      isCustomerVendorDisabled,
      updateCreditNoteJson,
      resetInvoiceDetails,
      setLineItems,
      setCreditsApplied,
      setCreditsAppliedCalculations,
      setSelectedInvoiceId,
      setSelectedInvoiceNumber,
      setLinkedDocumentDate,
    ],
  );

  // Invoice selection handler - mandatory
  const handleInvoiceSelect = useCallback(
    async (invoice: any) => {
      // CRITICAL: Clear old invoice data before selecting new invoice
      // Clear old invoice details
      resetInvoiceDetails();
      // Clear old line items and credits applied
      setLineItems([]);
      setCreditsApplied(null);
      setCreditsAppliedCalculations(null);
      updateCreditNoteJson({
        linked_invoice_id: undefined,
        zoho_invoiceid: undefined,
        invoiceId: undefined,
        reference_number: "",
        line_items: [],
      });

      // Cache the selected invoice
      selectedInvoiceCacheRef.current = invoice;

      const invoiceId = invoice.invoice_id || invoice.zoho_invoiceid || "";
      const invoiceNumber = invoice.invoice_number || "";
      const zohoInvoiceId = invoice.zoho_invoiceid || invoice.invoice_id || "";

      setSelectedInvoiceId(invoiceId);
      setSelectedInvoiceNumber(invoiceNumber);

      // Date field is now user-editable, no auto-population

      // Clear linked invoice error when invoice is selected
      clearFieldError("linked_invoice_id");

      // Fetch invoice details to get line items
      // Check if we already have the invoice details for this invoice ID
      if (zohoInvoiceId) {
        // If invoiceDetails already has data for this invoice, skip fetching
        if (invoiceDetails && invoiceDetails.invoice_id === zohoInvoiceId) {
          // Already have the data, line items will be processed by useEffect
          return;
        }

        try {
          clearInvoiceDetailsError();
          await fetchInvoiceDetails(zohoInvoiceId);
        } catch (error) {
          console.error("Error fetching invoice details:", error);
          showError("Failed to fetch invoice details. Please try again.");
        }
      }
    },
    [
      fetchInvoiceDetails,
      clearFieldError,
      clearInvoiceDetailsError,
      showError,
      invoiceDetails,
      updateCreditNoteJson,
      resetInvoiceDetails,
      setLineItems,
      setCreditsApplied,
      setCreditsAppliedCalculations,
      setSelectedInvoiceId,
      setSelectedInvoiceNumber,
    ],
  );

  // Vendor selection handler
  const handleVendorSelect = useCallback(
    (vendor: VendorListItem) => {
      // Prevent selection if customer/vendor fields are disabled (e.g., when posted)
      if (isCustomerVendorDisabled) {
        return;
      }

      // CRITICAL: Clear all bill-related state when vendor changes
      // Clear bill selection
      setSelectedBillId("");
      setSelectedBillNumber("");
      // Clear bill details
      setBillDetails(null);
      setBillDetailsError(null);
      // Clear line items
      setLineItems([]);
      updateCreditNoteJson({
        linked_bill_id: undefined,
        zoho_billid: undefined,
        billId: undefined,
        bill_id: undefined,
        reference_number: "",
        line_items: [],
      });
      // Clear credits applied
      setCreditsApplied(null);
      setCreditsAppliedCalculations(null);
      // Clear linked document date
      setLinkedDocumentDate("");

      // Cache the selected vendor
      selectedVendorCacheRef.current = vendor;

      setSelectedVendorId(vendor.zoho_vendorid || "");
      setSelectedVendorName(vendor.vendor_legal_name || "");
      const vendorCountry = vendor.country || "";
      const vendorState = vendorCountry === "IN" ? vendor.state || "" : "";

      const updates: Partial<CreditNoteDataEntity> = {
        vendor_id: vendor.zoho_vendorid || "",
        vendor_name: vendor.vendor_legal_name || "",
        gst_treatment: (vendor.gst_treatment || "") as any,
        gst_no: vendor.gstin || "",
        country: vendorCountry,
        state: vendorState,
      };

      // Auto-populate currency if vendor has one.
      if (vendor.currency_id) {
        const vendorCurrency = findCurrencyById(vendor.currency_id);
        if (vendorCurrency) {
          handleCurrencyChange(vendor.currency_id);
        }
      }

      updateCreditNoteJsonWithTracking(updates);

      // Clear vendor error when vendor is selected
      clearFieldError("vendor_id");
      clearFieldError("country");
      clearFieldError("state");
    },
    [
      updateCreditNoteJsonWithTracking,
      findCurrencyById,
      handleCurrencyChange,
      clearFieldError,
      isCustomerVendorDisabled,
      setLineItems,
      setCreditsApplied,
      setCreditsAppliedCalculations,
      setSelectedBillId,
      setSelectedBillNumber,
      setBillDetails,
      setBillDetailsError,
      updateCreditNoteJson,
      setLinkedDocumentDate,
    ],
  );

  // Bill selection handler - mandatory for vendor credits
  const handleBillSelect = useCallback(
    async (bill: VendorBillListItem) => {
      // CRITICAL: Clear old bill data before selecting new bill
      // Clear old line items and credits applied
      setLineItems([]);
      setCreditsApplied(null);
      setCreditsAppliedCalculations(null);
      setBillDetails(null);
      setBillDetailsError(null);
      updateCreditNoteJson({
        linked_bill_id: undefined,
        zoho_billid: undefined,
        billId: undefined,
        bill_id: undefined,
        reference_number: "",
        line_items: [],
      });

      // Cache the selected bill
      selectedBillCacheRef.current = bill;

      const billId = bill.bill_id || bill.zoho_billid || "";
      const billNumber = bill.bill_number || "";

      setSelectedBillId(billId);
      setSelectedBillNumber(billNumber);

      // Date field is now user-editable, no auto-population

      // Clear linked bill error when bill is selected
      clearFieldError("linked_bill_id");

      // Fetch bill details to get line items
      // Check if we already have the bill details for this bill ID
      if (billId) {
        // If billDetails already has data for this bill, skip fetching
        if (billDetails && billDetails.bill_id === billId) {
          return;
        }

        try {
          setBillDetailsLoading(true);
          setBillDetailsError(null);
          const details = await fetchBillDetails(billId);
          setBillDetails(details);
        } catch (error: any) {
          console.error("Error fetching bill details:", error);
          setBillDetailsError(error.message || "Failed to fetch bill details");
          showError("Failed to fetch bill details. Please try again.");
        } finally {
          setBillDetailsLoading(false);
        }
      }
    },
    [
      updateCreditNoteJsonWithTracking,
      fetchBillDetails,
      clearFieldError,
      showError,
      billDetails,
      updateCreditNoteJson,
      setLineItems,
      setCreditsApplied,
      setCreditsAppliedCalculations,
      setSelectedBillId,
      setSelectedBillNumber,
      setBillDetails,
      setBillDetailsError,
    ],
  );

  // Process bill details when fetched and map to credit note line items (similar to invoice details)
  useEffect(() => {
    // CRITICAL: NEVER import line items for posted documents - always use the JSON from credit note detail
    // For posted documents, line items are already in the database and should never be overwritten
    // This check MUST be at the very beginning, before any processing
    // Check both isPostedToZoho flag AND document status directly (defensive check)
    const isDocumentPosted =
      isPostedToZoho ||
      (creditNoteDocument?.status === "1" &&
        creditNoteDocument?.posted_to_zoho_date !== null &&
        creditNoteDocument?.posted_to_zoho_date !== undefined);

    if (isDocumentPosted) {
      // For posted documents, we never update line items from bill details
      // Line items must always come from creditNoteJson (saved in database)
      return;
    }

    if (billDetails && billDetails.line_items && isVendorCreditNote) {
      // CRITICAL: Skip if line items have been manually edited (to prevent overwriting user edits)
      if (isUpdatingLineItemsRef.current) {
        return;
      }

      // Avoid overwriting saved drafts with fetched bill lines
      // Check current creditNoteJson state (not just initialCreditNoteJson) to prevent
      // overwriting line items after applying approved changes
      const statusValue = Number(creditNoteDocument?.status);
      const isDraftStatus =
        statusValue === 0 || statusValue === 3 || statusValue === 4;
      const hasSavedLines =
        (creditNoteJson?.line_items?.length || 0) > 0 ||
        (initialCreditNoteJson?.line_items?.length || 0) > 0;
      const skipImportFromBill = hasSavedLines && isDraftStatus;
      if (skipImportFromBill) {
        return;
      }
      const zohoBillId =
        billDetails.bill_id ||
        creditNoteJson?.linked_bill_id ||
        creditNoteJson?.zoho_billid ||
        "";

      // Map bill line items to credit note line items
      // Exclude line_item_id as per requirements
      // For vendor credits, only rate and qty will be editable later
      const mappedLineItems: CreditNoteLineItemEntity[] =
        billDetails.line_items.map((item: any) => {
          const creditNoteLineItem: CreditNoteLineItemEntity = {
            description: item.description || item.name || "",
            quantity: item.quantity || 0,
            rate: item.rate || 0,
            account_id: item.account_id || "",
            account_name: item.account_name || "",
            hsn_or_sac: item.hsn_or_sac || "",
            tax_id: item.tax_id || "",
            tax_name: item.tax_name || "",
            tax_percentage: item.tax_percentage || 0,
            tax_amount: item.tax_amount || 0,
            item_total: item.item_total || 0,
            type: 1, // Credit note line item type
          };

          // Add TDS fields if present
          if (item.tds_tax_id) {
            creditNoteLineItem.tds_tax_id = item.tds_tax_id;
          }
          if (item.tds_tax_percentage) {
            creditNoteLineItem.tds_tax_percentage = item.tds_tax_percentage;
          }

          // Explicitly exclude line_item_id - do not include it
          return creditNoteLineItem;
        });

      // Update credit note JSON with mapped line items
      const billId =
        billDetails.bill_id || creditNoteJson?.linked_bill_id || "";
      const billNumber =
        billDetails.bill_number || creditNoteJson?.reference_number || "";

      // ZOUR-65 — same as the customer-credit path above: a VENDOR credit
      // follows the BILL it is raised against, so the parent's registration is
      // what the locked picker must print.
      const parentLocationId = billDetails.location_id ?? null;

      const updates: Partial<CreditNoteDataEntity> = {
        linked_bill_id: billId,
        zoho_billid: zohoBillId,
        billId: zohoBillId, // Add bill_id at top level for vendor credits
        bill_id: zohoBillId, // Add bill_id at top level for vendor credits
        reference_number: billNumber,
        location_id: parentLocationId,
        // CRITICAL: NEVER include line_items for posted documents
        // Even though we should have returned early, this is a defensive check
        ...(isDocumentPosted ? {} : { line_items: mappedLineItems }),
      };

      // Date field is now user-editable, no auto-population

      // Only update if there are actual changes to avoid infinite loops
      // CRITICAL: Exclude line_items comparison for posted documents
      const hasChanges =
        creditNoteJson?.linked_bill_id !== billId ||
        creditNoteJson?.zoho_billid !== zohoBillId ||
        creditNoteJson?.billId !== zohoBillId ||
        creditNoteJson?.bill_id !== zohoBillId ||
        creditNoteJson?.reference_number !== billNumber ||
        (creditNoteJson?.location_id ?? null) !== parentLocationId ||
        // Never compare line_items for posted documents
        (!isDocumentPosted &&
          JSON.stringify(creditNoteJson?.line_items) !==
            JSON.stringify(mappedLineItems));

      if (hasChanges) {
        updateCreditNoteJson(updates);
      }

      // Check if TDS is applicable (if any line item has TDS)
      const hasTDS = mappedLineItems.some(
        (item) => item.tds_tax_id && item.tds_tax_percentage,
      );
      setIsTDSApplicable(hasTDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    billDetails,
    isVendorCreditNote,
    isPostedToZoho,
    creditNoteDocument,
    // NOTE: creditNoteJson removed from deps to prevent overwriting manual edits
    // This useEffect should only run when billDetails changes, not when creditNoteJson changes
  ]);

  // Also check TDS applicability from current line items (for vendor credits)
  useEffect(() => {
    if (isVendorCreditNote && lineItems.length > 0) {
      const hasTDS = lineItems.some(
        (item) => item.tds_tax_id && item.tds_tax_percentage,
      );
      setIsTDSApplicable(hasTDS);
    }
  }, [lineItems, isVendorCreditNote]);

  // Do NOT transform invoice line items - users must add line items manually

  const handleAddLineItem = useCallback(() => {
    const newItem: CreditNoteLineItemEntity = {
      description: "",
      quantity: 1,
      rate: 0,
      type: 1,
    };
    setEditingLineItemIndex(-1);
    setEditingLineItemData(newItem);
    setOriginalAccountName("");
    setIsEditLineItemModalOpen(true);
  }, []);

  const handleEditLineItem = useCallback(
    (index: number) => {
      setEditingLineItemIndex(index);
      const lineItemData = { ...lineItems[index] };
      setOriginalAccountName(lineItemData.account_name || "");
      setEditingLineItemData(lineItemData);
      setIsEditLineItemModalOpen(true);
    },
    [lineItems],
  );

  const handleDeleteLineItem = useCallback(
    (index: number) => {
      const updatedItems = lineItems.filter((_, i) => i !== index);

      // Track line item deletion
      if (
        originalCreditNoteJson?.line_items &&
        originalCreditNoteJson.line_items[index]
      ) {
        const deletedItem = originalCreditNoteJson.line_items[index];
        setTrackedChanges((prevTrackedChanges) => {
          const updatedChanges = prevTrackedChanges.filter(
            (change) => !change.field.startsWith(`line_items[${index}]`),
          );

          // Track that the line item was removed
          updatedChanges.push({
            field: `line_items[${index}].quantity`,
            old: formatValueForTracking(deletedItem.quantity),
            new: "removed",
          });

          return updatedChanges;
        });
      }

      // Mark that we're updating lineItems ourselves
      isUpdatingLineItemsRef.current = true;
      // Update both lineItems state and creditNoteJson (same pattern as InvoiceReviewPage)
      setLineItems(updatedItems);
      updateCreditNoteJson({
        line_items: updatedItems,
      });
    },
    [
      lineItems,
      updateCreditNoteJson,
      originalCreditNoteJson,
      formatValueForTracking,
    ],
  );

  const handleSaveLineItem = useCallback(() => {
    if (editingLineItemData && creditNoteJson) {
      // Ensure tax_amount, tds_tax_amount, and item_total are calculated if missing (rounded to 2 decimals)
      const itemSubTotal = roundToTwoDecimals(
        (editingLineItemData.quantity || 0) * (editingLineItemData.rate || 0),
      );
      const calculatedTaxAmount = roundToTwoDecimals(
        (itemSubTotal * (editingLineItemData.tax_percentage || 0)) / 100,
      );
      const taxAmount =
        editingLineItemData.tax_amount !== undefined &&
        editingLineItemData.tax_amount !== null
          ? roundToTwoDecimals(editingLineItemData.tax_amount)
          : calculatedTaxAmount;

      // Calculate TDS amount if TDS is present (for vendor credits)
      const calculatedTdsAmount =
        editingLineItemData.tds_tax_id && editingLineItemData.tds_tax_percentage
          ? roundToTwoDecimals(
              (itemSubTotal * editingLineItemData.tds_tax_percentage) / 100,
            )
          : 0;
      const tdsAmount =
        editingLineItemData.tds_tax_amount !== undefined &&
        editingLineItemData.tds_tax_amount !== null
          ? roundToTwoDecimals(editingLineItemData.tds_tax_amount)
          : calculatedTdsAmount;

      let updatedLineItem: CreditNoteLineItemEntity;

      // Note: bill_id should only be at the top level for vendor credits, not in line items
      // For customer credits, invoice_id can be in line items
      if (isVendorCreditNote) {
        // For vendor credits, remove bill_id from line item if it exists
        // bill_id should only be at the top level of creditNoteJson
        const { bill_id, ...lineItemWithoutBillId } = editingLineItemData;
        updatedLineItem = {
          ...lineItemWithoutBillId,
          item_total: itemSubTotal,
          tax_amount: isGstEnabled ? taxAmount : 0,
          tds_tax_amount: tdsAmount,
        };
      } else {
        // For customer credits, use invoice_id in line items
        const zohoInvoiceId =
          creditNoteJson.zoho_invoiceid ||
          creditNoteJson.linked_invoice_id ||
          "";

        updatedLineItem = {
          ...editingLineItemData,
          invoice_id: zohoInvoiceId,
          item_total: itemSubTotal,
          tax_amount: isGstEnabled ? taxAmount : 0,
          // TDS is typically not applicable to customer credits (invoices), but preserve if present
          tds_tax_amount: tdsAmount,
        };
      }

      let updatedItems: CreditNoteLineItemEntity[];

      if (editingLineItemIndex === -1) {
        updatedItems = [...lineItems, updatedLineItem];
      } else if (editingLineItemIndex !== null) {
        updatedItems = [...lineItems];
        updatedItems[editingLineItemIndex] = updatedLineItem;
      } else {
        updatedItems = lineItems;
      }

      // Track line item changes
      if (originalCreditNoteJson) {
        setTrackedChanges((prevTrackedChanges) => {
          const updatedChanges = prevTrackedChanges.filter((change) => {
            if (editingLineItemIndex === -1) {
              // For new items, don't filter (will add new entries)
              return true;
            } else if (editingLineItemIndex !== null) {
              // For editing, remove existing changes for this line item
              return !change.field.startsWith(
                `line_items[${editingLineItemIndex}]`,
              );
            }
            return true;
          });
          const newChanges: Array<{
            field: string;
            old: string;
            new: string;
          }> = [];

          if (editingLineItemIndex === -1) {
            // New line item added
            const newIndex = updatedItems.length - 1;
            newChanges.push({
              field: `line_items[${newIndex}].quantity`,
              old: "empty",
              new: formatValueForTracking(updatedLineItem.quantity),
            });
            newChanges.push({
              field: `line_items[${newIndex}].rate`,
              old: "empty",
              new: formatValueForTracking(updatedLineItem.rate),
            });
          } else if (
            editingLineItemIndex !== null &&
            originalCreditNoteJson.line_items
          ) {
            // Editing existing line item - compare with original
            const originalLineItem =
              originalCreditNoteJson.line_items[editingLineItemIndex];

            if (originalLineItem) {
              // Track quantity change
              if (originalLineItem.quantity !== updatedLineItem.quantity) {
                newChanges.push({
                  field: `line_items[${editingLineItemIndex}].quantity`,
                  old: formatValueForTracking(originalLineItem.quantity),
                  new: formatValueForTracking(updatedLineItem.quantity),
                });
              }

              // Track rate change
              if (originalLineItem.rate !== updatedLineItem.rate) {
                newChanges.push({
                  field: `line_items[${editingLineItemIndex}].rate`,
                  old: formatValueForTracking(originalLineItem.rate),
                  new: formatValueForTracking(updatedLineItem.rate),
                });
              }
            }
          }

          return [...updatedChanges, ...newChanges];
        });
      }

      // Mark that we're updating lineItems ourselves
      isUpdatingLineItemsRef.current = true;
      // Update both lineItems state and creditNoteJson (same pattern as InvoiceReviewPage)
      setLineItems(updatedItems);
      // IMPORTANT: updateCreditNoteJson expects an object with line_items property, not the array directly!
      updateCreditNoteJson({
        line_items: updatedItems,
      });

      setIsEditLineItemModalOpen(false);
      setEditingLineItemIndex(null);
      setEditingLineItemData(null);
      setOriginalAccountName("");
    }
  }, [
    editingLineItemData,
    editingLineItemIndex,
    lineItems,
    creditNoteJson,
    isVendorCreditNote,
    updateCreditNoteJson,
    roundToTwoDecimals,
    isGstEnabled,
    originalCreditNoteJson,
    formatValueForTracking,
  ]);

  const handleCancelEdit = () => {
    setIsEditLineItemModalOpen(false);
    setEditingLineItemIndex(null);
    setEditingLineItemData(null);
    setOriginalAccountName("");
  };

  // Query handlers
  const handleQueryClick = (queryId: number) => {
    setSelectedQueryId(queryId);
    setIsQueryDetailModalOpen(true);
  };

  const handleQueryRaiseModalOpen = () => {
    setIsQueryRaiseModalOpen(true);
  };

  // Check if there are any unresolved queries
  const hasUnresolvedQueries = queriesEntities.some(
    (q: any) => q.status !== "resolved",
  );
  const unresolvedCount = queriesEntities.filter(
    (q: any) => q.status !== "resolved",
  ).length;

  const handleLineItemDataChange = (newData: any) => {
    setEditingLineItemData(newData);
  };

  // Format amount helper
  const formatAmount = useCallback((amount: number) => {
    return `${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  // Helper function to format date as DD/MM/YYYY
  const formatDateDDMMYYYY = useCallback(
    (dateString: string | null | undefined): string => {
      if (!dateString) {
        return "";
      }

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "";
        }

        const pad2 = (value: number): string => {
          const asString = String(value);
          return asString.length >= 2 ? asString : "0" + asString;
        };
        const day = pad2(date.getDate());
        const month = pad2(date.getMonth() + 1);
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
      } catch (error) {
        return "";
      }
    },
    [],
  );

  const parseTaxRateFromName = (taxName: string): number | null => {
    const match = taxName?.match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : null;
  };

  const formatRate = (rate: number): string => {
    const fixed = Number(rate.toFixed(2));
    return Number.isInteger(fixed) ? fixed.toString() : fixed.toString();
  };

  const calculateTaxBreakdown = (
    items: any[],
  ): {
    rows: { label: string; amount: number }[];
    totalTax: number;
  } => {
    const taxGroups: Record<
      string,
      { type: "IGST" | "GST"; rate: number; amount: number }
    > = {};

    items.forEach((item) => {
      const taxName = (item.tax_name || "").toString();
      console.log("TaxName", taxName);
      const taxPercentageRaw = item.tax_percentage;
      const taxRateFromPercentage =
        taxPercentageRaw !== undefined &&
        taxPercentageRaw !== null &&
        !isNaN(Number(taxPercentageRaw))
          ? parseFloat(taxPercentageRaw)
          : null;
      const parsedRateFromName = parseTaxRateFromName(taxName);
      const taxRate =
        taxRateFromPercentage !== null
          ? taxRateFromPercentage
          : parsedRateFromName !== null && !isNaN(parsedRateFromName)
            ? parsedRateFromName
            : 0;

      if (!taxRate) return;

      // Determine tax type based only on tax_name
      let taxType: "IGST" | "GST";
      const taxNameUpper = taxName.toUpperCase();

      if (taxNameUpper.includes("IGST")) {
        taxType = "IGST";
      } else {
        // Default to GST (will be split into SGST and CGST)
        taxType = "GST";
      }

      const taxableAmount = (item.rate || 0) * (item.quantity || 0);
      const taxAmount = (taxableAmount * taxRate) / 100;

      const key = `${taxType}-${taxRate}`;
      if (!taxGroups[key]) {
        taxGroups[key] = { type: taxType, rate: taxRate, amount: 0 };
      }
      taxGroups[key].amount += taxAmount;
    });

    const rows: { label: string; amount: number }[] = [];
    const taxGroupList: Array<{
      type: "IGST" | "GST";
      rate: number;
      amount: number;
    }> = [];
    Object.keys(taxGroups).forEach((key) => {
      taxGroupList.push(taxGroups[key]);
    });

    taxGroupList.forEach((group) => {
      if (group.type === "IGST") {
        rows.push({
          label: `IGST ${formatRate(group.rate)}%`,
          amount: group.amount,
        });
      } else {
        const halfRate = group.rate / 2;
        const halfAmount = group.amount / 2;
        rows.push(
          {
            label: `SGST ${formatRate(halfRate)}%`,
            amount: halfAmount,
          },
          {
            label: `CGST ${formatRate(halfRate)}%`,
            amount: halfAmount,
          },
        );
      }
    });

    const totalTax = taxGroupList.reduce(
      (sum, group) => sum + group.amount,
      0,
    );

    return { rows, totalTax };
  };

  // Memoize tax breakdown calculation to prevent infinite loops
  const taxBreakdown = useMemo(() => {
    return calculateTaxBreakdown(lineItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  // Helper function to format date as "March 15, 2024"
  const formatDate = useCallback(
    (
      dateString: string | null | undefined,
      fallback: string = "Not provided",
    ): string => {
      if (!dateString || dateString === "Not provided") {
        return fallback;
      }

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return fallback;
        }

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch (error) {
        return fallback;
      }
    },
    [],
  );

  // Show loading state during initial load
  // Show loader until all data is loaded (permissions, document, and review statuses)
  if (isInitialLoading || isDataLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-center justify-center bg-default-background">
          <Loader />
          <span className="mt-4 text-body font-body text-subtext-color">
            Loading credit note...
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-start bg-default-background px-6 py-6">
          <Alert variant="error" title="Error Loading Credit Note">
            {error}
          </Alert>
          <Button
            variant="neutral-secondary"
            onClick={() => navigate("/dashboard/credit")}
            className="mt-4"
          >
            Back to Credit Notes
          </Button>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        {/* Header */}
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border bg-default-background px-6 py-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-secondary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/dashboard/credit")}
            >
              Go Back
            </Button>
            <Breadcrumbs>
              {isManualCreation ? (
                <Breadcrumbs.Item onClick={() => navigate("/dashboard/credit")}>
                  Create Credit Note
                </Breadcrumbs.Item>
              ) : (
                <>
                  <Breadcrumbs.Item
                    onClick={() => navigate("/dashboard/credit")}
                  >
                    Review Credit Note
                  </Breadcrumbs.Item>
                  {creditNoteJson?.creditnote_number && (
                    <Breadcrumbs.Item>
                      {creditNoteJson.creditnote_number}
                    </Breadcrumbs.Item>
                  )}
                </>
              )}
            </Breadcrumbs>
          </div>
          <div className="flex items-center gap-2">
            {!disableAutoSave && isAutoSaving && (
              <span className="text-caption font-caption text-subtext-color">
                Auto-saving...
              </span>
            )}
            {!disableAutoSave && lastSaved && (
              <span className="text-caption font-caption text-subtext-color">
                Last saved: {lastSaved.toLocaleTimeString()}
              </span>
            )}
            {/* Show Save as Draft only for manual creation */}
            {isManualCreation && !isPostedToZoho && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherSave />}
                onClick={handleSaveAsDraft}
                disabled={
                  isReadOnly ||
                  operationsLoading ||
                  !canCreate ||
                  isDataLoading ||
                  isReviewPending
                }
              >
                Save as Draft
              </Button>
            )}
            {/* Hide Reject for edit mode (posted to zoho) or manual creation */}
            {!isEditMode && !isManualCreation && !isPostedToZoho && (
              <Button
                variant="destructive-primary"
                icon={<FeatherX />}
                onClick={handleReject}
                disabled={isReadOnly || operationsLoading || isReviewPending}
              >
                Reject
              </Button>
            )}
            {/* Delete Credit Note: draft always; posted only when user has delete_after_push */}
            {documentId && (isPostedToZoho ? canDeleteAfterPush : true) && (
              <Button
                variant="destructive-primary"
                icon={<FeatherTrash2 />}
                onClick={handleDeleteCreditNote}
                disabled={
                  isReadOnly ||
                  operationsLoading ||
                  isDataLoading ||
                  isReviewPending
                }
              >
                Delete Credit Note
              </Button>
            )}
            {/* Update / Approve button */}
            <div className="flex items-center gap-2">
              <Button
                icon={<FeatherCheck />}
                onClick={handleApproveAndPost}
                disabled={
                  isReadOnly ||
                  operationsLoading ||
                  hasUnresolvedQueries ||
                  isDataLoading ||
                  isReviewPending ||
                  (isEditMode ? !canUpdateAfterPush : !canPushToZoho)
                }
              >
                {isEditMode ? "Update" : "Approve & Post"}
              </Button>
            </div>
            {/* Hide Raise Query for manual creation */}
            {!isManualCreation && (
              <div className="flex items-center gap-2">
                <Button
                  variant="brand-secondary"
                  icon={<FeatherMessageCircle />}
                  onClick={handleQueryRaiseModalOpen}
                  disabled={isReadOnly || operationsLoading || isReviewPending}
                >
                  Raise a Query
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-y-auto">
          {/* Closed month banner - show when document is in a closed month */}
          {isMonthClosed === true && (
            <div className="w-full px-6 pt-4 pb-2">
              <ClosedMonthBanner entityLabel="Credit Note" />
            </div>
          )}

          {/* Review Status Banner - Show pending/rejected always, show approved temporarily */}
          {review && (
            <div className="w-full px-6 pt-4 pb-4">
              {(review.status === "pending" ||
                review.status === "rejected" ||
                (review.status === "approved" && showApprovedBanner)) && (
                <ReviewStatusBanner
                  review={review}
                  moduleCode={MODULE_CODES.CREDIT_NOTE}
                  currentUserId={user?.id ? parseInt(user.id, 10) : null}
                  onResubmit={() => {
                    if (review.status === "rejected") {
                      setIsReviewModalOpen(true);
                    }
                  }}
                />
              )}
            </div>
          )}

          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 w-full">
            <div className="flex w-full items-start gap-6">
              {/* PDF Viewer - Only show if s3_bucket is not null */}
              {!isManualCreation &&
              creditNoteDocument?.s3_bucket !== null &&
              creditNoteDocument?.s3_bucket !== undefined &&
              (signedUrl || documentId) ? (
                <DocumentViewer
                  documentId={documentId ? parseInt(documentId) : undefined}
                  documentType="bill" // Using "bill" as workaround until credit_note is supported
                  pdfUrl={signedUrl || undefined}
                  fileName={fileName || "credit-note.pdf"}
                  className="w-1/2"
                  onError={(error: any) => console.error("PDF Error:", error)}
                />
              ) : null}

              {/* Form Section */}
              <div
                className={`flex flex-col items-start gap-6 self-stretch px-6 py-6 ${
                  isManualCreation ||
                  creditNoteDocument?.s3_bucket === null ||
                  creditNoteDocument?.s3_bucket === undefined ||
                  !(signedUrl || documentId)
                    ? "w-full"
                    : "w-1/2"
                } overflow-y-auto`}
              >
                {/* Display validation errors with ErrorBanner component */}
                <div ref={errorBannerRef} className="w-full pb-2">
                  <ErrorBanner
                    errors={errors}
                    title="Please fix the following errors:"
                    autoScrollOnMount={true}
                    context="invoice"
                  />
                </div>

                {/* Show alert when there are unresolved queries */}
                {hasUnresolvedQueries && (
                  <Alert variant="warning" title="Unresolved Queries">
                    This credit note has {unresolvedCount} unresolved{" "}
                    {unresolvedCount === 1 ? "query" : "queries"}.{" "}
                    Please {getApproveAndPostQueriesMessage(accountingBackend)}
                  </Alert>
                )}

                {/* Credit Note Type Toggle - Only for manual creation */}
                {isManualCreation && (
                  <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-4">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Credit Note Type <span className="text-error-600">*</span>
                    </span>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="creditNoteType"
                          value="customer"
                          checked={!isVendorCreditNote}
                          onChange={() => {
                            // Set type to customer
                            setCreditNoteType("customer");
                            // Clear vendor data for customer credit note
                            const updatedJson = { ...creditNoteJson };
                            delete updatedJson.vendor_id;
                            delete updatedJson.vendor_name;
                            delete updatedJson.linked_bill_id;
                            delete updatedJson.zoho_billid;
                            updatedJson.linked_invoice_id = undefined;
                            updatedJson.reference_number = "";
                            updatedJson.line_items = [];
                            updateCreditNoteJson(updatedJson);
                            setSelectedVendorId("");
                            setSelectedVendorName("");
                            setSelectedBillId("");
                            setSelectedBillNumber("");
                            setSelectedCustomerId("");
                            setSelectedCustomerName("");
                            setSelectedInvoiceId("");
                            setSelectedInvoiceNumber("");
                            clearErrors();
                          }}
                          className="w-4 h-4"
                          disabled={isOtherFieldsDisabled}
                        />
                        <span className="text-body font-body text-default-font">
                          Customer Credit Note
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="creditNoteType"
                          value="vendor"
                          checked={isVendorCreditNote}
                          onChange={() => {
                            // Set type to vendor
                            setCreditNoteType("vendor");
                            // Clear customer data for vendor credit note
                            const updatedJson = { ...creditNoteJson };
                            delete updatedJson.customer_id;
                            delete updatedJson.customer_name;
                            delete updatedJson.linked_invoice_id;
                            delete updatedJson.zoho_invoiceid;
                            updatedJson.linked_bill_id = undefined;
                            updatedJson.reference_number = "";
                            updatedJson.line_items = [];
                            updateCreditNoteJson(updatedJson);
                            setSelectedCustomerId("");
                            setSelectedCustomerName("");
                            setSelectedInvoiceId("");
                            setSelectedInvoiceNumber("");
                            setSelectedVendorId("");
                            setSelectedVendorName("");
                            setSelectedBillId("");
                            setSelectedBillNumber("");
                            clearErrors();
                          }}
                          className="w-4 h-4"
                          disabled={isOtherFieldsDisabled}
                        />
                        <span className="text-body font-body text-default-font">
                          Vendor Credit Note
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Note Summary */}
                <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        Note Summary
                      </span>
                      <Badge variant="success">Credit Note</Badge>
                      <StatusBadgeForCreditAndDebitNotes
                        status={String(creditNoteDocument?.status || "0")}
                        type="credit"
                      />
                    </div>
                  </div>
                  <div className="flex w-full flex-col items-start gap-4">
                    {/* Row 1: Credit Note Number, Reference No and Date (when no PDF or manual creation) */}
                    {isManualCreation ||
                    !creditNoteDocument?.s3_bucket ||
                    creditNoteDocument?.s3_bucket === null ||
                    creditNoteDocument?.s3_bucket === undefined ? (
                      <>
                        <div className="flex w-full items-start gap-4">
                          <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                            <TextField
                              className="h-auto w-full"
                              label={
                                <>
                                  Credit Note Number{" "}
                                  <span className="text-error-600">*</span>
                                </>
                              }
                              helpText={errors.creditnote_number}
                              error={!!errors.creditnote_number}
                            >
                              <TextField.Input
                                placeholder="Enter credit note number"
                                value={
                                  creditNoteJson?.creditnote_number ||
                                  creditNoteJson?.vendor_credit_number ||
                                  ""
                                }
                                disabled={isOtherFieldsDisabled}
                                onChange={(e) => {
                                  updateCreditNoteJsonWithTracking({
                                    creditnote_number: e.target.value,
                                  });
                                  clearFieldError("creditnote_number");
                                }}
                              />
                            </TextField>
                          </div>
                        </div>
                        <div className="flex w-full items-start gap-4">
                          <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                            <TextField
                              className="h-auto w-full"
                              label={
                                <>
                                  Reference No{" "}
                                  <span className="text-error-600">*</span>
                                </>
                              }
                              helpText={errors.reference_number}
                              error={!!errors.reference_number}
                            >
                              <TextField.Input
                                placeholder={
                                  isVendorCreditNote
                                    ? "Select a bill to auto-populate"
                                    : "Select an invoice to auto-populate"
                                }
                                value={creditNoteJson?.reference_number || ""}
                                disabled={true}
                                readOnly={true}
                              />
                            </TextField>
                          </div>
                          <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                            <TextField
                              className="h-auto w-full"
                              label={
                                <>
                                  Date <span className="text-error-600">*</span>
                                </>
                              }
                              icon={<FeatherCalendar />}
                              helpText={errors.date || ""}
                              error={!!errors.date}
                            >
                              <TextField.Input
                                type="date"
                                placeholder="YYYY-MM-DD"
                                value={creditNoteJson?.date || ""}
                                onChange={(e) => {
                                  updateCreditNoteJsonWithTracking({
                                    date: e.target.value,
                                  });
                                  clearFieldError("date");
                                }}
                              />
                            </TextField>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Credit Note Number{" "}
                            <span className="text-error-600">*</span>
                          </span>
                          <TextField
                            className="h-auto grow shrink-0 basis-0"
                            label=""
                            helpText={errors.creditnote_number}
                            error={!!errors.creditnote_number}
                          >
                            <TextField.Input
                              placeholder="Enter credit note number"
                              value={
                                creditNoteJson?.creditnote_number ||
                                creditNoteJson?.vendor_credit_number ||
                                ""
                              }
                              disabled={isOtherFieldsDisabled}
                              onChange={(e) => {
                                updateCreditNoteJsonWithTracking({
                                  creditnote_number: e.target.value,
                                });
                                clearFieldError("creditnote_number");
                              }}
                            />
                          </TextField>
                        </div>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Reference No{" "}
                            <span className="text-error-600">*</span>
                          </span>
                          <TextField
                            className="h-auto grow shrink-0 basis-0"
                            label=""
                            helpText={errors.reference_number}
                            error={!!errors.reference_number}
                          >
                            <TextField.Input
                              placeholder={
                                isVendorCreditNote
                                  ? "Select a bill to auto-populate"
                                  : "Select an invoice to auto-populate"
                              }
                              value={creditNoteJson?.reference_number || ""}
                              disabled={true}
                              readOnly={true}
                            />
                          </TextField>
                        </div>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Date <span className="text-error-600">*</span>
                          </span>
                          <TextField
                            className="h-auto grow shrink-0 basis-0"
                            label=""
                            icon={<FeatherCalendar />}
                            helpText={errors.date || ""}
                            error={!!errors.date}
                          >
                            <TextField.Input
                              type="date"
                              placeholder="YYYY-MM-DD"
                              value={creditNoteJson?.date || ""}
                              onChange={(e) => {
                                updateCreditNoteJsonWithTracking({
                                  date: e.target.value,
                                });
                                clearFieldError("date");
                              }}
                            />
                          </TextField>
                        </div>
                      </>
                    )}
                    {/* Row 2: Customer/Vendor and GSTIN side by side (when no PDF or manual creation) */}
                    {isManualCreation ||
                    !creditNoteDocument?.s3_bucket ||
                    creditNoteDocument?.s3_bucket === null ||
                    creditNoteDocument?.s3_bucket === undefined ? (
                      <div className="flex w-full items-start gap-4">
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            {!isVendorCreditNote ? (
                              <>
                                Customer{" "}
                                <span className="text-error-600">*</span>
                              </>
                            ) : (
                              <>
                                Vendor <span className="text-error-600">*</span>
                              </>
                            )}
                          </span>
                          {!isVendorCreditNote ? (
                            <SearchableDropdown
                              fetchItems={async (searchTerm, limit, offset) => {
                                return await fetchCustomers(
                                  searchTerm,
                                  limit,
                                  offset,
                                  selectedCustomerId ||
                                    creditNoteJson?.customer_id ||
                                    undefined,
                                  selectedCustomerName ||
                                    creditNoteJson?.customer_name ||
                                    undefined,
                                );
                              }}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={
                                    isCustomerVendorDisabled || customersLoading
                                  }
                                >
                                  {customersLoading
                                    ? "Loading customers..."
                                    : selectedCustomerName ||
                                      creditNoteJson?.customer_name ||
                                      "Select customer"}
                                </Button>
                              }
                              getSearchableText={(customer: CustomerListItem) =>
                                customer.customer_name ||
                                customer.company_name ||
                                ""
                              }
                              renderItem={(customer: CustomerListItem) =>
                                customer.customer_name ||
                                customer.company_name ||
                                ""
                              }
                              getItemKey={(customer: CustomerListItem) =>
                                customer.zoho_customerid ||
                                customer.id?.toString() ||
                                ""
                              }
                              onSelect={(customer: CustomerListItem) => {
                                handleCustomerSelect(customer);
                                clearFieldError("customer_id");
                              }}
                              selectedKey={
                                selectedCustomerId ||
                                creditNoteJson?.customer_id ||
                                ""
                              }
                              searchPlaceholder="Search customers..."
                              emptyMessage="No customers available"
                              noResultsMessage="No customers match your search"
                            />
                          ) : (
                            <SearchableDropdown
                              fetchItems={async (searchTerm, limit, offset) => {
                                return await fetchVendors(
                                  searchTerm,
                                  limit,
                                  offset,
                                  selectedVendorId ||
                                    creditNoteJson?.vendor_id ||
                                    undefined,
                                  selectedVendorName ||
                                    creditNoteJson?.vendor_name ||
                                    undefined,
                                );
                              }}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={
                                    isCustomerVendorDisabled || vendorsLoading
                                  }
                                >
                                  {vendorsLoading
                                    ? "Loading vendors..."
                                    : selectedVendorName ||
                                      creditNoteJson?.vendor_name ||
                                      "Select vendor"}
                                </Button>
                              }
                              getSearchableText={(vendor: VendorListItem) =>
                                vendor.vendor_legal_name || ""
                              }
                              renderItem={(vendor: VendorListItem) =>
                                vendor.vendor_legal_name || ""
                              }
                              getItemKey={(vendor: VendorListItem) =>
                                vendor.zoho_vendorid ||
                                vendor.id?.toString() ||
                                ""
                              }
                              onSelect={(vendor: VendorListItem) => {
                                handleVendorSelect(vendor);
                                clearFieldError("vendor_id");
                              }}
                              selectedKey={
                                selectedVendorId ||
                                creditNoteJson?.vendor_id ||
                                ""
                              }
                              searchPlaceholder="Search vendors..."
                              emptyMessage="No vendors available"
                              noResultsMessage="No vendors match your search"
                            />
                          )}
                          {errors.customer_id && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.customer_id}
                            </span>
                          )}
                          {errors.vendor_id && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.vendor_id}
                            </span>
                          )}
                        </div>
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <TextField
                            className="h-auto w-full"
                            label={
                              <>
                                GSTIN{" "}
                                {(isVendorCreditNote
                                  ? creditNoteJson?.vendor_id
                                  : creditNoteJson?.customer_id) &&
                                  creditNoteJson?.gst_treatment ===
                                    "business_gst" && (
                                    <span className="text-error-600">*</span>
                                  )}
                              </>
                            }
                            helpText={errors.gst_no}
                            error={!!errors.gst_no}
                          >
                            <TextField.Input
                              placeholder="Enter GSTIN"
                              value={creditNoteJson?.gst_no || ""}
                              onChange={(e) => {
                                updateCreditNoteJson({
                                  gst_no: e.target.value,
                                });
                                clearFieldError("gst_no");
                              }}
                              disabled={
                                isReadOnly ||
                                creditNoteJson?.gst_treatment !== "business_gst"
                              }
                            />
                          </TextField>
                        </div>
                      </div>
                    ) : (
                      <>
                        {!isVendorCreditNote ? (
                          <div className="flex w-full items-center gap-4">
                            <span className="w-32 flex-none text-body font-body text-subtext-color">
                              Customer <span className="text-error-600">*</span>
                            </span>
                            <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                              <SearchableDropdown
                                fetchItems={async (
                                  searchTerm,
                                  limit,
                                  offset,
                                ) => {
                                  return await fetchCustomers(
                                    searchTerm,
                                    limit,
                                    offset,
                                    selectedCustomerId ||
                                      creditNoteJson?.customer_id ||
                                      undefined,
                                    selectedCustomerName ||
                                      creditNoteJson?.customer_name ||
                                      undefined,
                                  );
                                }}
                                trigger={
                                  <Button
                                    variant="neutral-secondary"
                                    className="w-full justify-between h-8"
                                    iconRight={<FeatherChevronDown />}
                                    disabled={
                                      isEditingDisabled || customersLoading
                                    }
                                  >
                                    {customersLoading
                                      ? "Loading customers..."
                                      : selectedCustomerName ||
                                        creditNoteJson?.customer_name ||
                                        "Select customer"}
                                  </Button>
                                }
                                getSearchableText={(
                                  customer: CustomerListItem,
                                ) =>
                                  customer.customer_name ||
                                  customer.company_name ||
                                  ""
                                }
                                renderItem={(customer: CustomerListItem) =>
                                  customer.customer_name ||
                                  customer.company_name ||
                                  ""
                                }
                                getItemKey={(customer: CustomerListItem) =>
                                  customer.zoho_customerid ||
                                  customer.id?.toString() ||
                                  ""
                                }
                                onSelect={(customer: CustomerListItem) => {
                                  handleCustomerSelect(customer);
                                  clearFieldError("customer_id");
                                }}
                                selectedKey={
                                  selectedCustomerId ||
                                  creditNoteJson?.customer_id ||
                                  ""
                                }
                                searchPlaceholder="Search customers..."
                                emptyMessage="No customers available"
                                noResultsMessage="No customers match your search"
                              />
                              {errors.customer_id && (
                                <span className="text-caption font-caption text-error-700">
                                  {errors.customer_id}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex w-full items-center gap-4">
                            <span className="w-32 flex-none text-body font-body text-subtext-color">
                              Vendor <span className="text-error-600">*</span>
                            </span>
                            <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                              <SearchableDropdown
                                fetchItems={async (
                                  searchTerm,
                                  limit,
                                  offset,
                                ) => {
                                  return await fetchVendors(
                                    searchTerm,
                                    limit,
                                    offset,
                                    selectedVendorId ||
                                      creditNoteJson?.vendor_id ||
                                      undefined,
                                    selectedVendorName ||
                                      creditNoteJson?.vendor_name ||
                                      undefined,
                                  );
                                }}
                                trigger={
                                  <Button
                                    variant="neutral-secondary"
                                    className="w-full justify-between h-8"
                                    iconRight={<FeatherChevronDown />}
                                    disabled={
                                      isCustomerVendorDisabled || vendorsLoading
                                    }
                                  >
                                    {vendorsLoading
                                      ? "Loading vendors..."
                                      : selectedVendorName ||
                                        creditNoteJson?.vendor_name ||
                                        "Select vendor"}
                                  </Button>
                                }
                                getSearchableText={(vendor: VendorListItem) =>
                                  vendor.vendor_legal_name || ""
                                }
                                renderItem={(vendor: VendorListItem) =>
                                  vendor.vendor_legal_name || ""
                                }
                                getItemKey={(vendor: VendorListItem) =>
                                  vendor.zoho_vendorid ||
                                  vendor.id?.toString() ||
                                  ""
                                }
                                onSelect={(vendor: VendorListItem) => {
                                  handleVendorSelect(vendor);
                                  clearFieldError("vendor_id");
                                }}
                                selectedKey={
                                  selectedVendorId ||
                                  creditNoteJson?.vendor_id ||
                                  ""
                                }
                                searchPlaceholder="Search vendors..."
                                emptyMessage="No vendors available"
                                noResultsMessage="No vendors match your search"
                              />
                              {errors.vendor_id && (
                                <span className="text-caption font-caption text-error-700">
                                  {errors.vendor_id}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            GSTIN{" "}
                            {(isVendorCreditNote
                              ? creditNoteJson?.vendor_id
                              : creditNoteJson?.customer_id) &&
                              creditNoteJson?.gst_treatment ===
                                "business_gst" && (
                                <span className="text-error-600">*</span>
                              )}
                          </span>
                          <TextField
                            className="h-auto grow shrink-0 basis-0"
                            label=""
                            helpText={errors.gst_no}
                            error={!!errors.gst_no}
                          >
                            <TextField.Input
                              placeholder="Enter GSTIN"
                              value={creditNoteJson?.gst_no || ""}
                              onChange={(e) => {
                                updateCreditNoteJson({
                                  gst_no: e.target.value,
                                });
                                clearFieldError("gst_no");
                              }}
                              disabled={
                                isReadOnly ||
                                creditNoteJson?.gst_treatment !== "business_gst"
                              }
                            />
                          </TextField>
                        </div>
                      </>
                    )}
                    {/* Row 3: Country and State side by side (when no PDF or manual creation) */}
                    {isManualCreation ||
                    !creditNoteDocument?.s3_bucket ||
                    creditNoteDocument?.s3_bucket === null ||
                    creditNoteDocument?.s3_bucket === undefined ? (
                      <div className="flex w-full items-start gap-4">
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            Country <span className="text-error-600">*</span>
                          </span>
                          <SearchableDropdown
                            items={COUNTRIES}
                            disabled={true}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="w-full justify-between h-8"
                                iconRight={<FeatherChevronDown />}
                                disabled={true}
                              >
                                {creditNoteJson?.country
                                  ? findCountryByCode(creditNoteJson.country)
                                      ?.name || "Select country"
                                  : "Select country"}
                              </Button>
                            }
                            getSearchableText={(country) => country.name}
                            renderItem={(country) => country.name}
                            getItemKey={(country) => country.code}
                            onSelect={() => {}}
                            selectedKey={creditNoteJson?.country || ""}
                            searchPlaceholder="Search countries..."
                            emptyMessage="No countries available"
                            noResultsMessage="No countries match your search"
                          />
                          {errors.country && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.country}
                            </span>
                          )}
                        </div>
                        <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            State{" "}
                            {creditNoteJson?.country === "IN" && (
                              <span className="text-error-600">*</span>
                            )}
                          </span>
                          <SearchableDropdown
                            items={INDIAN_STATES}
                            disabled={true}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="w-full justify-between h-8"
                                iconRight={<FeatherChevronDown />}
                                disabled={true}
                              >
                                {creditNoteJson?.state
                                  ? findIndianStateByCode(creditNoteJson.state)
                                      ?.name || creditNoteJson.state
                                  : "Select state"}
                              </Button>
                            }
                            getSearchableText={(state) => state.name}
                            renderItem={(state) => state.name}
                            getItemKey={(state) => state.code}
                            onSelect={() => {}}
                            selectedKey={creditNoteJson?.state || ""}
                            searchPlaceholder="Search states..."
                            emptyMessage="No states available"
                            noResultsMessage="No states match your search"
                          />
                          {errors.state && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.state}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Country <span className="text-error-600">*</span>
                          </span>
                          <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                            <SearchableDropdown
                              items={COUNTRIES}
                              disabled={true}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={true}
                                >
                                  {creditNoteJson?.country
                                    ? findCountryByCode(creditNoteJson.country)
                                        ?.name || "Select country"
                                    : "Select country"}
                                </Button>
                              }
                              getSearchableText={(country) => country.name}
                              renderItem={(country) => country.name}
                              getItemKey={(country) => country.code}
                              onSelect={() => {}}
                              selectedKey={creditNoteJson?.country || ""}
                              searchPlaceholder="Search countries..."
                              emptyMessage="No countries available"
                              noResultsMessage="No countries match your search"
                            />
                            {errors.country && (
                              <span className="text-caption font-caption text-error-700">
                                {errors.country}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            State{" "}
                            {creditNoteJson?.country === "IN" && (
                              <span className="text-error-600">*</span>
                            )}
                          </span>
                          <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                            <SearchableDropdown
                              items={INDIAN_STATES}
                              disabled={true}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={true}
                                >
                                  {creditNoteJson?.state
                                    ? findIndianStateByCode(
                                        creditNoteJson.state,
                                      )?.name || creditNoteJson.state
                                    : "Select state"}
                                </Button>
                              }
                              getSearchableText={(state) => state.name}
                              renderItem={(state) => state.name}
                              getItemKey={(state) => state.code}
                              onSelect={() => {}}
                              selectedKey={creditNoteJson?.state || ""}
                              searchPlaceholder="Search states..."
                              emptyMessage="No states available"
                              noResultsMessage="No states match your search"
                            />
                            {errors.state && (
                              <span className="text-caption font-caption text-error-700">
                                {errors.state}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Currency field */}
                    {isManualCreation ||
                    !creditNoteDocument?.s3_bucket ||
                    creditNoteDocument?.s3_bucket === null ||
                    creditNoteDocument?.s3_bucket === undefined ? (
                      <div className="flex flex-col gap-1 max-w-md">
                        <span className="text-caption-bold font-caption-bold text-default-font">
                          Currency <span className="text-error-600">*</span>
                        </span>
                        <SearchableDropdown
                          items={currencies}
                          loading={currenciesLoading}
                          trigger={
                            <Button
                              variant="neutral-secondary"
                              className="h-8 w-full justify-between"
                              iconRight={<FeatherChevronDown />}
                              disabled={
                                isOtherFieldsDisabled || currenciesLoading
                              }
                            >
                              {currenciesLoading
                                ? "Loading currencies..."
                                : creditNoteJson?.currency_id
                                  ? findCurrencyById(creditNoteJson.currency_id)
                                      ?.currency_name_formatted ||
                                    creditNoteJson.currency_code
                                  : "Select currency"}
                            </Button>
                          }
                          getSearchableText={(currency) =>
                            currency.currency_name_formatted ||
                            currency.currency_name
                          }
                          renderItem={(currency) =>
                            currency.currency_name_formatted ||
                            currency.currency_name
                          }
                          getItemKey={(currency) => currency.currency_id}
                          onSelect={(currency) => {
                            handleCurrencyChange(currency.currency_id);
                            clearFieldError("currency_id");
                          }}
                          selectedKey={creditNoteJson?.currency_id || ""}
                          searchPlaceholder="Search currencies..."
                          emptyMessage="No currencies available"
                          noResultsMessage="No currencies match your search"
                        />
                        {errors.currency_id && (
                          <span className="text-caption font-caption text-error-700">
                            {errors.currency_id}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex w-full items-start gap-4">
                        <span className="w-32 flex-none text-body font-body text-subtext-color pt-2">
                          Currency <span className="text-error-600">*</span>
                        </span>
                        <div className="flex flex-col gap-1 max-w-md">
                          <SearchableDropdown
                            items={currencies}
                            loading={currenciesLoading}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="h-8 w-full justify-between"
                                iconRight={<FeatherChevronDown />}
                                disabled={
                                  isEditingDisabled || currenciesLoading
                                }
                              >
                                {currenciesLoading
                                  ? "Loading currencies..."
                                  : creditNoteJson?.currency_id
                                    ? findCurrencyById(
                                        creditNoteJson.currency_id,
                                      )?.currency_name_formatted ||
                                      creditNoteJson.currency_code
                                    : "Select currency"}
                              </Button>
                            }
                            getSearchableText={(currency) =>
                              currency.currency_name_formatted ||
                              currency.currency_name
                            }
                            renderItem={(currency) =>
                              currency.currency_name_formatted ||
                              currency.currency_name
                            }
                            getItemKey={(currency) => currency.currency_id}
                            onSelect={(currency) => {
                              handleCurrencyChange(currency.currency_id);
                              clearFieldError("currency_id");
                            }}
                            selectedKey={creditNoteJson?.currency_id || ""}
                            searchPlaceholder="Search currencies..."
                            emptyMessage="No currencies available"
                            noResultsMessage="No currencies match your search"
                          />
                          {errors.currency_id && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.currency_id}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                  <div className="flex w-full flex-col items-start gap-3">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {isVendorCreditNote
                        ? "Bill Mapping Mode"
                        : "Invoice Mapping Mode"}
                    </span>
                    {/* Invoice selection for customer credits */}
                    {!isVendorCreditNote ? (
                      isManualCreation ||
                      !creditNoteDocument?.s3_bucket ||
                      creditNoteDocument?.s3_bucket === null ||
                      creditNoteDocument?.s3_bucket === undefined ? (
                        <div className="flex w-full flex-col gap-1 max-w-md">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            Linked Invoice{" "}
                            <span className="text-error-600">*</span>
                          </span>
                          <SearchableDropdown
                            fetchItems={async (searchTerm, limit, offset) => {
                              if (
                                !selectedCustomerId &&
                                !creditNoteJson?.customer_id
                              ) {
                                return { items: [], hasMore: false };
                              }
                              return await fetchInvoices(
                                selectedCustomerId ||
                                  creditNoteJson?.customer_id ||
                                  "",
                                searchTerm,
                                limit,
                                offset,
                                selectedInvoiceId ||
                                  creditNoteJson?.linked_invoice_id ||
                                  undefined,
                                selectedInvoiceNumber || undefined,
                              );
                            }}
                            trigger={
                              <Button
                                variant="neutral-secondary"
                                className="w-full justify-between h-8"
                                iconRight={<FeatherChevronDown />}
                                disabled={
                                  isEditingDisabled ||
                                  isPostedToZoho ||
                                  invoicesLoading ||
                                  invoiceDetailsLoading ||
                                  (!selectedCustomerName &&
                                    !creditNoteJson?.customer_name)
                                }
                              >
                                {invoicesLoading || invoiceDetailsLoading
                                  ? "Loading invoices..."
                                  : selectedInvoiceNumber
                                    ? selectedInvoiceNumber || ""
                                    : "Select invoice"}
                              </Button>
                            }
                            getSearchableText={(
                              invoice: InvoiceListItemEntity,
                            ) => invoice.invoice_number || ""}
                            renderItem={(invoice: InvoiceListItemEntity) =>
                              `${invoice.invoice_number || ""}${
                                invoice.date ? ` - ${invoice.date}` : ""
                              }${
                                invoice.total !== undefined &&
                                invoice.total !== null
                                  ? ` - Total: ${
                                      invoice.currency_symbol || ""
                                    }${invoice.total.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : ""
                              }${
                                invoice.balance !== undefined &&
                                invoice.balance !== null
                                  ? ` - Balance: ${
                                      invoice.currency_symbol || ""
                                    }${invoice.balance.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : ""
                              }`
                            }
                            getItemKey={(invoice: InvoiceListItemEntity) =>
                              invoice.invoice_id || invoice.id?.toString() || ""
                            }
                            onSelect={(invoice: InvoiceListItemEntity) => {
                              handleInvoiceSelect(invoice);
                              clearFieldError("linked_invoice_id");
                            }}
                            selectedKey={
                              selectedInvoiceId ||
                              creditNoteJson?.linked_invoice_id ||
                              ""
                            }
                            searchPlaceholder="Search invoices..."
                            emptyMessage={
                              !selectedCustomerName &&
                              !creditNoteJson?.customer_name
                                ? "Select a customer first"
                                : "No invoices with outstanding balance available"
                            }
                            noResultsMessage="No invoices match your search"
                          />
                          {errors.linked_invoice_id && (
                            <span className="text-caption font-caption text-error-700">
                              {errors.linked_invoice_id}
                            </span>
                          )}
                          {invoiceDetailsError && (
                            <span className="text-caption font-caption text-error-700">
                              {invoiceDetailsError}
                            </span>
                          )}
                          {(invoiceDetails?.balance !== undefined ||
                            invoiceDetails?.total !== undefined ||
                            invoiceDetails?.date) && (
                            <div className="flex flex-col gap-1 mt-1">
                              {invoiceDetails?.date && (
                                <div className="flex items-center gap-2">
                                  <span className="text-caption font-caption text-subtext-color">
                                    Invoice Date:
                                  </span>
                                  <span className="text-caption-bold font-caption-bold text-default-font">
                                    {formatDateDDMMYYYY(invoiceDetails.date)}
                                  </span>
                                </div>
                              )}
                              {invoiceDetails?.total !== undefined && (
                                <div className="flex items-center gap-2">
                                  <span className="text-caption font-caption text-subtext-color">
                                    Invoice Total:
                                  </span>
                                  <span className="text-caption-bold font-caption-bold text-default-font">
                                    {creditNoteJson?.currency_symbol || "₹"}
                                    {formatAmount(invoiceDetails.total)}
                                  </span>
                                </div>
                              )}
                              {invoiceDetails?.balance !== undefined && (
                                <div className="flex items-center gap-2">
                                  <span className="text-caption font-caption text-subtext-color">
                                    Invoice Balance:
                                  </span>
                                  <span className="text-caption-bold font-caption-bold text-brand-600">
                                    {creditNoteJson?.currency_symbol || "₹"}
                                    {formatAmount(invoiceDetails.balance)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex w-full items-center gap-4">
                          <span className="w-32 flex-none text-body font-body text-subtext-color">
                            Linked Invoice{" "}
                            <span className="text-error-600">*</span>
                          </span>
                          <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                            <SearchableDropdown
                              fetchItems={async (searchTerm, limit, offset) => {
                                if (
                                  !selectedCustomerId &&
                                  !creditNoteJson?.customer_id
                                ) {
                                  return { items: [], hasMore: false };
                                }
                                return await fetchInvoices(
                                  selectedCustomerId ||
                                    creditNoteJson?.customer_id ||
                                    "",
                                  searchTerm,
                                  limit,
                                  offset,
                                );
                              }}
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  className="w-full justify-between h-8"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={
                                    isOtherFieldsDisabled ||
                                    isPostedToZoho ||
                                    invoicesLoading ||
                                    invoiceDetailsLoading ||
                                    (!selectedCustomerName &&
                                      !creditNoteJson?.customer_name)
                                  }
                                >
                                  {invoicesLoading || invoiceDetailsLoading
                                    ? "Loading invoices..."
                                    : selectedInvoiceNumber
                                      ? selectedInvoiceNumber || ""
                                      : "Select invoice"}
                                </Button>
                              }
                              getSearchableText={(
                                invoice: InvoiceListItemEntity,
                              ) => invoice.invoice_number || ""}
                              renderItem={(invoice: InvoiceListItemEntity) =>
                                `${invoice.invoice_number || ""}${
                                  invoice.date ? ` - ${invoice.date}` : ""
                                }${
                                  invoice.total !== undefined &&
                                  invoice.total !== null
                                    ? ` - Total: ${
                                        invoice.currency_symbol || ""
                                      }${invoice.total.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`
                                    : ""
                                }${
                                  invoice.balance !== undefined &&
                                  invoice.balance !== null
                                    ? ` - Balance: ${
                                        invoice.currency_symbol || ""
                                      }${invoice.balance.toLocaleString(
                                        "en-IN",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      )}`
                                    : ""
                                }`
                              }
                              getItemKey={(invoice: InvoiceListItemEntity) =>
                                invoice.invoice_id ||
                                invoice.id?.toString() ||
                                ""
                              }
                              onSelect={(invoice: InvoiceListItemEntity) => {
                                handleInvoiceSelect(invoice);
                                clearFieldError("linked_invoice_id");
                              }}
                              selectedKey={
                                selectedInvoiceId ||
                                creditNoteJson?.linked_invoice_id ||
                                ""
                              }
                              searchPlaceholder="Search invoices..."
                              emptyMessage={
                                !selectedCustomerName &&
                                !creditNoteJson?.customer_name
                                  ? "Select a customer first"
                                  : "No invoices with outstanding balance available"
                              }
                              noResultsMessage="No invoices match your search"
                            />
                            {errors.linked_invoice_id && (
                              <span className="text-caption font-caption text-error-700">
                                {errors.linked_invoice_id}
                              </span>
                            )}
                            {invoiceDetailsError && (
                              <span className="text-caption font-caption text-error-700">
                                {invoiceDetailsError}
                              </span>
                            )}
                            {(invoiceDetails?.balance !== undefined ||
                              invoiceDetails?.total !== undefined) && (
                              <div className="flex flex-col gap-1 mt-1">
                                {invoiceDetails?.total !== undefined && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-caption font-caption text-subtext-color">
                                      Invoice Total:
                                    </span>
                                    <span className="text-caption-bold font-caption-bold text-default-font">
                                      {creditNoteJson?.currency_symbol || "₹"}
                                      {formatAmount(invoiceDetails.total)}
                                    </span>
                                  </div>
                                )}
                                {invoiceDetails?.balance !== undefined && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-caption font-caption text-subtext-color">
                                      Invoice Balance:
                                    </span>
                                    <span className="text-caption-bold font-caption-bold text-brand-600">
                                      {creditNoteJson?.currency_symbol || "₹"}
                                      {formatAmount(invoiceDetails.balance)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    ) : (
                      <>
                        {/* Bill selection for vendor credits */}
                        {isManualCreation ||
                        !creditNoteDocument?.s3_bucket ||
                        creditNoteDocument?.s3_bucket === null ||
                        creditNoteDocument?.s3_bucket === undefined ? (
                          <div className="flex w-full items-start gap-4">
                            <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                              <span className="text-caption-bold font-caption-bold text-default-font">
                                Linked Bill{" "}
                                <span className="text-error-600">*</span>
                              </span>
                              <SearchableDropdown
                                fetchItems={async (
                                  searchTerm,
                                  limit,
                                  offset,
                                ) => {
                                  if (
                                    !selectedVendorId &&
                                    !creditNoteJson?.vendor_id
                                  ) {
                                    return { items: [], hasMore: false };
                                  }
                                  return await fetchVendorBills(
                                    selectedVendorId ||
                                      creditNoteJson?.vendor_id ||
                                      "",
                                    searchTerm,
                                    limit,
                                    offset,
                                    selectedBillId ||
                                      creditNoteJson?.linked_bill_id ||
                                      undefined,
                                    selectedBillNumber || undefined,
                                  );
                                }}
                                trigger={
                                  <Button
                                    variant="neutral-secondary"
                                    className="w-full justify-between h-8"
                                    iconRight={<FeatherChevronDown />}
                                    disabled={
                                      isOtherFieldsDisabled ||
                                      isPostedToZoho ||
                                      vendorBillsLoading ||
                                      billDetailsLoading ||
                                      (!selectedVendorId &&
                                        !creditNoteJson?.vendor_id)
                                    }
                                  >
                                    {vendorBillsLoading || billDetailsLoading
                                      ? "Loading bills..."
                                      : selectedBillNumber
                                        ? selectedBillNumber || ""
                                        : "Select bill"}
                                  </Button>
                                }
                                getSearchableText={(bill: VendorBillListItem) =>
                                  bill.bill_number || bill.bill_id || ""
                                }
                                renderItem={(bill: VendorBillListItem) =>
                                  `${bill.bill_number || bill.bill_id || ""}${
                                    bill.date ? ` - ${bill.date}` : ""
                                  }${
                                    bill.total !== undefined &&
                                    bill.total !== null
                                      ? ` - Total: ${
                                          bill.currency_code || ""
                                        }${bill.total.toLocaleString("en-IN", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}`
                                      : ""
                                  }${
                                    bill.balance !== undefined &&
                                    bill.balance !== null
                                      ? ` - Balance: ${
                                          bill.currency_code || ""
                                        }${bill.balance.toLocaleString(
                                          "en-IN",
                                          {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          },
                                        )}`
                                      : ""
                                  }`
                                }
                                getItemKey={(bill: VendorBillListItem) =>
                                  bill.bill_id || bill.zoho_billid || ""
                                }
                                onSelect={(bill: VendorBillListItem) => {
                                  handleBillSelect(bill);
                                  clearFieldError("linked_bill_id");
                                }}
                                selectedKey={
                                  selectedBillId ||
                                  creditNoteJson?.linked_bill_id ||
                                  creditNoteJson?.zoho_billid ||
                                  ""
                                }
                                searchPlaceholder="Search bills..."
                                emptyMessage={
                                  !selectedVendorName &&
                                  !creditNoteJson?.vendor_name
                                    ? "Select a vendor first"
                                    : "No bills with outstanding balance available"
                                }
                                noResultsMessage="No bills match your search"
                              />
                              {errors.linked_bill_id && (
                                <span className="text-caption font-caption text-error-700">
                                  {errors.linked_bill_id}
                                </span>
                              )}
                              {billDetailsError && (
                                <span className="text-caption font-caption text-error-700">
                                  {billDetailsError}
                                </span>
                              )}
                              {(billDetails?.balance !== undefined ||
                                billDetails?.total !== undefined ||
                                billDetails?.date) && (
                                <div className="flex flex-col gap-1 mt-1">
                                  {billDetails?.date && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-caption font-caption text-subtext-color">
                                        Bill Date:
                                      </span>
                                      <span className="text-caption-bold font-caption-bold text-default-font">
                                        {formatDateDDMMYYYY(billDetails.date)}
                                      </span>
                                    </div>
                                  )}
                                  {billDetails?.total !== undefined && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-caption font-caption text-subtext-color">
                                        Bill Total:
                                      </span>
                                      <span className="text-caption-bold font-caption-bold text-default-font">
                                        {creditNoteJson?.currency_symbol || "₹"}
                                        {formatAmount(billDetails.total)}
                                      </span>
                                    </div>
                                  )}
                                  {billDetails?.balance !== undefined && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-caption font-caption text-subtext-color">
                                        Bill Balance:
                                      </span>
                                      <span className="text-caption-bold font-caption-bold text-brand-600">
                                        {creditNoteJson?.currency_symbol || "₹"}
                                        {formatAmount(billDetails.balance)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Destination of Supply for vendor credits - side by side */}
                            <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                              <span className="text-caption-bold font-caption-bold text-default-font">
                                Destination of Supply{" "}
                                <span className="text-error-600">*</span>
                              </span>
                              <SearchableDropdown
                                items={INDIAN_STATES}
                                disabled={true}
                                trigger={
                                  <Button
                                    variant="neutral-secondary"
                                    className="w-full justify-between h-8"
                                    iconRight={<FeatherChevronDown />}
                                    disabled={true}
                                  >
                                    {(
                                      billDetails?.destination_of_supply ||
                                      creditNoteJson?.destination_of_supply
                                    )
                                      ? findIndianStateByCode(
                                          billDetails?.destination_of_supply ||
                                            creditNoteJson?.destination_of_supply,
                                        )?.name ||
                                        billDetails?.destination_of_supply ||
                                        creditNoteJson?.destination_of_supply
                                      : "Select destination of supply"}
                                  </Button>
                                }
                                getSearchableText={(state) => state.name}
                                renderItem={(state) => state.name}
                                getItemKey={(state) => state.code}
                                onSelect={() => {}}
                                selectedKey={
                                  billDetails?.destination_of_supply ||
                                  creditNoteJson?.destination_of_supply ||
                                  ""
                                }
                                searchPlaceholder="Search states..."
                                emptyMessage="No states available"
                                noResultsMessage="No states match your search"
                              />
                              {errors.destination_of_supply && (
                                <span className="text-caption font-caption text-error-700">
                                  {errors.destination_of_supply}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex w-full items-center gap-4">
                              <span className="w-32 flex-none text-body font-body text-subtext-color">
                                Linked Bill{" "}
                                <span className="text-error-600">*</span>
                              </span>
                              <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                                <SearchableDropdown
                                  fetchItems={async (
                                    searchTerm,
                                    limit,
                                    offset,
                                  ) => {
                                    if (
                                      !selectedVendorId &&
                                      !creditNoteJson?.vendor_id
                                    ) {
                                      return { items: [], hasMore: false };
                                    }
                                    return await fetchVendorBills(
                                      selectedVendorId ||
                                        creditNoteJson?.vendor_id ||
                                        "",
                                      searchTerm,
                                      limit,
                                      offset,
                                    );
                                  }}
                                  trigger={
                                    <Button
                                      variant="neutral-secondary"
                                      className="w-full justify-between h-8"
                                      iconRight={<FeatherChevronDown />}
                                      disabled={
                                        isEditingDisabled ||
                                        isPostedToZoho ||
                                        vendorBillsLoading ||
                                        billDetailsLoading ||
                                        (!selectedVendorId &&
                                          !creditNoteJson?.vendor_id)
                                      }
                                    >
                                      {vendorBillsLoading || billDetailsLoading
                                        ? "Loading bills..."
                                        : selectedBillNumber
                                          ? selectedBillNumber || ""
                                          : "Select bill"}
                                    </Button>
                                  }
                                  getSearchableText={(
                                    bill: VendorBillListItem,
                                  ) => bill.bill_number || bill.bill_id || ""}
                                  renderItem={(bill: VendorBillListItem) =>
                                    `${bill.bill_number || bill.bill_id || ""}${
                                      bill.date ? ` - ${bill.date}` : ""
                                    }${
                                      bill.total !== undefined &&
                                      bill.total !== null
                                        ? ` - Total: ${
                                            bill.currency_code || ""
                                          }${bill.total.toLocaleString(
                                            "en-IN",
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          )}`
                                        : ""
                                    }${
                                      bill.balance !== undefined &&
                                      bill.balance !== null
                                        ? ` - Balance: ${
                                            bill.currency_code || ""
                                          }${bill.balance.toLocaleString(
                                            "en-IN",
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          )}`
                                        : ""
                                    }`
                                  }
                                  getItemKey={(bill: VendorBillListItem) =>
                                    bill.bill_id || bill.zoho_billid || ""
                                  }
                                  onSelect={(bill: VendorBillListItem) => {
                                    handleBillSelect(bill);
                                    clearFieldError("linked_bill_id");
                                  }}
                                  selectedKey={
                                    selectedBillId ||
                                    creditNoteJson?.linked_bill_id ||
                                    creditNoteJson?.zoho_billid ||
                                    ""
                                  }
                                  searchPlaceholder="Search bills..."
                                  emptyMessage={
                                    !selectedVendorName &&
                                    !creditNoteJson?.vendor_name
                                      ? "Select a vendor first"
                                      : "No bills with outstanding balance available"
                                  }
                                  noResultsMessage="No bills match your search"
                                />
                                {errors.linked_bill_id && (
                                  <span className="text-caption font-caption text-error-700">
                                    {errors.linked_bill_id}
                                  </span>
                                )}
                                {billDetailsError && (
                                  <span className="text-caption font-caption text-error-700">
                                    {billDetailsError}
                                  </span>
                                )}
                                {(billDetails?.balance !== undefined ||
                                  billDetails?.total !== undefined ||
                                  billDetails?.date) && (
                                  <div className="flex flex-col gap-1 mt-1">
                                    {billDetails?.date && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-caption font-caption text-subtext-color">
                                          Bill Date:
                                        </span>
                                        <span className="text-caption-bold font-caption-bold text-default-font">
                                          {formatDateDDMMYYYY(billDetails.date)}
                                        </span>
                                      </div>
                                    )}
                                    {billDetails?.total !== undefined && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-caption font-caption text-subtext-color">
                                          Bill Total:
                                        </span>
                                        <span className="text-caption-bold font-caption-bold text-default-font">
                                          {creditNoteJson?.currency_symbol ||
                                            "₹"}
                                          {formatAmount(billDetails.total)}
                                        </span>
                                      </div>
                                    )}
                                    {billDetails?.balance !== undefined && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-caption font-caption text-subtext-color">
                                          Bill Balance:
                                        </span>
                                        <span className="text-caption-bold font-caption-bold text-brand-600">
                                          {creditNoteJson?.currency_symbol ||
                                            "₹"}
                                          {formatAmount(billDetails.balance)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* Destination of Supply for vendor credits - after bills dropdown */}
                            <div className="flex w-full items-center gap-4">
                              <span className="w-32 flex-none text-body font-body text-subtext-color">
                                Destination of Supply{" "}
                                <span className="text-error-600">*</span>
                              </span>
                              <div className="grow shrink-0 basis-0 flex flex-col gap-1">
                                <SearchableDropdown
                                  items={INDIAN_STATES}
                                  disabled={true}
                                  trigger={
                                    <Button
                                      variant="neutral-secondary"
                                      className="w-full justify-between h-8"
                                      iconRight={<FeatherChevronDown />}
                                      disabled={true}
                                    >
                                      {(
                                        billDetails?.destination_of_supply ||
                                        creditNoteJson?.destination_of_supply
                                      )
                                        ? findIndianStateByCode(
                                            billDetails?.destination_of_supply ||
                                              creditNoteJson?.destination_of_supply,
                                          )?.name ||
                                          billDetails?.destination_of_supply ||
                                          creditNoteJson?.destination_of_supply
                                        : "Select destination of supply"}
                                    </Button>
                                  }
                                  getSearchableText={(state) => state.name}
                                  renderItem={(state) => state.name}
                                  getItemKey={(state) => state.code}
                                  onSelect={() => {}}
                                  selectedKey={
                                    billDetails?.destination_of_supply ||
                                    creditNoteJson?.destination_of_supply ||
                                    ""
                                  }
                                  searchPlaceholder="Search states..."
                                  emptyMessage="No states available"
                                  noResultsMessage="No states match your search"
                                />
                                {errors.destination_of_supply && (
                                  <span className="text-caption font-caption text-error-700">
                                    {errors.destination_of_supply}
                                  </span>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Document Information */}
                <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Document Information
                  </span>
                  {isManualCreation ||
                  !creditNoteDocument?.s3_bucket ||
                  creditNoteDocument?.s3_bucket === null ||
                  creditNoteDocument?.s3_bucket === undefined ? (
                    <div className="flex w-full items-start gap-4 flex-wrap">
                      <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                        <TextField className="h-auto w-full" label="Reason">
                          <TextField.Input
                            placeholder="Enter reason code (optional)"
                            value={creditNoteJson?.reason_code || ""}
                            onChange={(e) =>
                              updateCreditNoteJson({
                                reason_code: e.target.value,
                              })
                            }
                            disabled={isOtherFieldsDisabled}
                          />
                        </TextField>
                      </div>
                      <div className="flex grow shrink-0 basis-0 flex-col gap-1">
                        <TextField className="h-auto w-full" label="Narration">
                          <TextField.Input
                            placeholder="Enter narration (optional)"
                            value={
                              creditNoteJson?.narration ||
                              creditNoteJson?.notes ||
                              ""
                            }
                            onChange={(e) =>
                              updateCreditNoteJson({
                                narration: e.target.value,
                                notes: e.target.value,
                              })
                            }
                            disabled={isOtherFieldsDisabled}
                          />
                        </TextField>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex w-full items-start gap-4 flex-wrap">
                        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4">
                          <div className="flex w-full items-center gap-4">
                            <span className="w-32 flex-none text-body font-body text-subtext-color">
                              Reason
                            </span>
                            <TextField
                              className="h-auto grow shrink-0 basis-0"
                              label=""
                            >
                              <TextField.Input
                                placeholder="Enter reason code (optional)"
                                value={creditNoteJson?.reason_code || ""}
                                onChange={(e) =>
                                  updateCreditNoteJson({
                                    reason_code: e.target.value,
                                  })
                                }
                                disabled={isOtherFieldsDisabled}
                              />
                            </TextField>
                          </div>
                        </div>
                      </div>
                      <div className="flex w-full items-center gap-4">
                        <span className="w-32 flex-none text-body font-body text-subtext-color">
                          Narration
                        </span>
                        <TextField
                          className="h-auto grow shrink-0 basis-0"
                          label=""
                        >
                          <TextField.Input
                            placeholder="Enter narration (optional)"
                            value={
                              creditNoteJson?.narration ||
                              creditNoteJson?.notes ||
                              ""
                            }
                            onChange={(e) =>
                              updateCreditNoteJson({
                                narration: e.target.value,
                                notes: e.target.value,
                              })
                            }
                            disabled={isOtherFieldsDisabled}
                          />
                        </TextField>
                      </div>
                    </>
                  )}
                  {/* <div className="flex w-full items-center gap-2 rounded-md bg-neutral-50 px-3 py-2">
                    <Avatar size="x-small" image="">
                      A
                    </Avatar>
                    <span className="text-caption font-caption text-subtext-color">
                      Last edited by Admin
                    </span>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Credits Applied Banner */}
            {creditsApplied &&
              creditsApplied.credits.length > 0 &&
              creditsAppliedCalculations && (
                <div className="flex w-full flex-col items-start gap-4 px-6">
                  <div
                    className={`flex w-full flex-col gap-4 rounded-md border border-solid px-4 py-3 ${
                      creditsAppliedCalculations.totalCreditsApplied >
                        creditsAppliedCalculations.invoiceBillTotal &&
                      creditsAppliedCalculations.invoiceBillTotal > 0
                        ? "border-error-200 bg-error-50"
                        : "border-neutral-200 bg-neutral-50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1">
                        <span className="text-heading-4 font-heading-4 text-default-font">
                          Credits Applied to{" "}
                          {creditsApplied.isVendorCredit ? "Bill" : "Invoice"}
                        </span>
                        {creditsAppliedCalculations.invoiceBillTotal > 0 && (
                          <span className="text-body font-body text-subtext-color">
                            Total{" "}
                            {creditsApplied.isVendorCredit ? "Bill" : "Invoice"}{" "}
                            Amount: {creditNoteJson?.currency_symbol || "₹"}
                            {creditsAppliedCalculations.invoiceBillTotal.toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-body font-body text-subtext-color">
                          Total Credits Applied:
                        </span>
                        <span className="text-heading-4 font-heading-4 text-default-font">
                          {creditNoteJson?.currency_symbol || "₹"}
                          {creditsAppliedCalculations.totalCreditsApplied.toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </span>
                        {creditsAppliedCalculations.invoiceBillTotal > 0 && (
                          <span className="text-body font-body text-subtext-color">
                            Remaining Balance:{" "}
                            {creditNoteJson?.currency_symbol || "₹"}
                            {creditsAppliedCalculations.remainingBalance.toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full flex-col gap-2 border-t border-solid border-neutral-border pt-4">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Applied Credit Notes:
                      </span>
                      <div className="flex flex-col gap-2">
                        {creditsApplied.credits.map((credit, index) => {
                          // Check if this credit note is the current one being edited
                          // Compare zoho_id with creditnote_id or vendor_credit_id
                          const currentZohoId = creditNoteDocument?.zoho_id;
                          const creditZohoId = creditsApplied.isVendorCredit
                            ? credit.vendor_credit_id
                            : credit.creditnote_id;
                          const isCurrentCredit =
                            !!currentZohoId && currentZohoId === creditZohoId;
                          const creditNumber = creditsApplied.isVendorCredit
                            ? credit.vendor_credit_number
                            : credit.creditnotes_number;
                          const creditDate = creditsApplied.isVendorCredit
                            ? credit.date
                            : credit.credited_date;
                          const creditAmount =
                            credit.amount_applied || credit.amount || 0;

                          return (
                            <div
                              key={index}
                              className={`flex items-center justify-between rounded-md border border-solid px-4 py-3 ${
                                isCurrentCredit
                                  ? "border-primary-500 bg-primary-50"
                                  : "border-neutral-border bg-default-background"
                              }`}
                            >
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-body font-body text-default-font">
                                    {creditNumber || "N/A"}
                                  </span>
                                  {isCurrentCredit && (
                                    <Badge variant="brand">Current</Badge>
                                  )}
                                </div>
                                <span className="text-caption font-caption text-subtext-color">
                                  Date:{" "}
                                  {creditDate
                                    ? formatDateDDMMYYYY(creditDate) ||
                                      creditDate
                                    : "N/A"}
                                </span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-body font-body text-default-font">
                                  {creditNoteJson?.currency_symbol || "₹"}
                                  {creditAmount.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                                <div className="flex items-center gap-2">
                                  {!isCurrentCredit && (
                                    <IconButton
                                      variant="neutral-secondary"
                                      size="small"
                                      icon={<FeatherExternalLink />}
                                      onClick={() =>
                                        handleNavigateToCreditNote(credit)
                                      }
                                      title="Navigate to credit note"
                                    />
                                  )}
                                  {/* {!isCurrentCredit && (
                                    <IconButton
                                      variant="neutral-secondary"
                                      size="small"
                                      icon={<FeatherTrash2 />}
                                      onClick={() =>
                                        confirmDeleteCreditApplied(credit)
                                      }
                                      // disabled={deleteLoading}
                                      title="Delete credit note application"
                                    />
                                  )} */}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* ZOUR-65: which GST registration this note belongs to.
                READ-ONLY, always. A note follows its parent document, and Zoho
                does NOT enforce that — it accepts a note on a different
                location silently and splits the tax the other way — so the
                server overwrites it with the parent's before pushing and this
                only reports the outcome. */}
            {hasMultipleLocations && (
              <div className="flex w-full flex-col items-start px-6 pb-6">
                <LocationPicker
                  locations={activeLocations}
                  value={creditNoteJson?.location_id ?? null}
                  lockedReason={
                    noteHasParent
                      ? `Follows the ${parentDocumentLabel} this note is raised against.`
                      : null
                  }
                  onChange={(locationId) =>
                    updateCreditNoteJsonWithTracking({ location_id: locationId })
                  }
                />
              </div>
            )}

            {showPreGstNotice && (
              <div className="flex w-full flex-col items-start px-6 pb-6">
                <GstPreRegistrationNotice
                  documentDate={
                    serverGstDecision?.effective_date ||
                    invoiceDetails?.date ||
                    creditNoteJson?.date ||
                    ""
                  }
                  // ZOUR-19 review 11: a vendor credit is the PURCHASE-side
                  // note, so it is the one that talks about input tax credit.
                  documentLabel={isVendorCreditNote ? "vendor credit" : "credit note"}
                  gstEnabledFrom={serverGstDecision?.gst_enabled_from ?? undefined}
                />
              </div>
            )}

            {/* Line Items Section */}
            <div className="flex w-full flex-col items-start gap-6 px-6 pb-6">
              <div className="flex w-full flex-col items-end gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Line Items
                    </span>
                    <Badge variant="success" icon={<FeatherCheck />}>
                      {lineItems.length} Lines Extracted
                    </Badge>
                  </div>
                  <Button
                    variant="neutral-primary"
                    size="small"
                    icon={<FeatherPlus />}
                    onClick={handleAddLineItem}
                    disabled={
                      isEditingDisabled ||
                      (isVendorCreditNote
                        ? !creditNoteJson?.linked_bill_id
                        : !creditNoteJson?.linked_invoice_id)
                    }
                  >
                    Add Line
                  </Button>
                </div>

                {/* Line Items Table */}
                <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border overflow-x-auto">
                  <div className="flex items-center gap-4 border-b border-solid border-neutral-border bg-default-background px-4 py-3 w-full text-caption-bold font-caption-bold text-subtext-color">
                    <div className="flex w-48 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Description
                      </span>
                    </div>
                    <div className="flex w-48 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        GL Account
                      </span>
                    </div>
                    <div className="flex w-24 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        HSN Code
                      </span>
                    </div>
                    <div className="flex w-20 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Qty
                      </span>
                    </div>
                    <div className="flex w-28 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Rate
                      </span>
                    </div>
                    {isGstEnabled && (
                      <div className="flex w-20 flex-none items-start">
                        <span className="text-body font-body text-default-font">
                          GST
                        </span>
                      </div>
                    )}
                    {isVendorCreditNote && isTDSApplicable && (
                      <div className="flex w-24 flex-none items-start">
                        <span className="text-body font-body text-default-font">
                          TDS
                        </span>
                      </div>
                    )}
                    <div className="flex w-32 flex-none items-start">
                      <span className="text-body font-body text-default-font">
                        Amount
                      </span>
                    </div>
                    <div className="flex flex-1 items-start justify-end">
                      <span className="text-body font-body text-default-font">
                        Actions
                      </span>
                    </div>
                  </div>

                  {/* Line Item Rows */}
                  {lineItems.length === 0 ? (
                    <div className="flex w-full items-center justify-center px-4 py-8">
                      <span className="text-body font-body text-subtext-color">
                        No line items. Click "Add Line" to add one.
                      </span>
                    </div>
                  ) : (
                    lineItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-4 border-b border-solid border-neutral-100 bg-white px-4 py-3 w-full"
                      >
                        <div className="flex w-48 flex-none items-center">
                          <span className="text-body-bold font-body-bold text-default-font">
                            {item.description || "No description"}
                          </span>
                        </div>
                        <div className="flex w-48 flex-none items-center gap-1">
                          <div className="inline-flex items-start rounded-md border border-solid border-success-100 bg-success-100 px-2 py-1 min-h-6">
                            <span className="text-caption font-caption text-success-800 whitespace-normal break-words leading-tight">
                              {item.account_name || "-"}
                            </span>
                          </div>
                        </div>
                        <div className="flex w-24 flex-none items-start">
                          <span className="text-body font-body text-default-font">
                            {item.hsn_or_sac || "N/A"}
                          </span>
                        </div>
                        <div className="flex w-20 flex-none items-start">
                          <span className="text-body font-body text-default-font">
                            {item.quantity || 0}
                          </span>
                        </div>
                        <div className="flex w-28 flex-none items-start">
                          <span className="text-body font-body text-default-font">
                            {creditNoteJson?.currency_symbol || "₹"}
                            {formatAmount(item.rate || 0)}
                          </span>
                        </div>
                        {isGstEnabled && (
                          <div className="flex w-20 flex-none items-start">
                            <span className="text-body font-body text-default-font">
                              {item.tax_percentage || 0}%
                            </span>
                          </div>
                        )}
                        {isVendorCreditNote && isTDSApplicable && (
                          <div className="flex w-24 flex-none items-start">
                            {item.tds_tax_id && item.tds_tax_percentage ? (
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-caption font-caption text-default-font">
                                  {item.tds_tax_name || "TDS"}
                                </span>
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {(() => {
                                    // Calculate TDS amount: (rate * quantity) * (tds_percentage / 100)
                                    const itemSubTotal =
                                      (item.rate || 0) * (item.quantity || 0);
                                    const tdsAmount =
                                      itemSubTotal *
                                      ((item.tds_tax_percentage || 0) / 100);
                                    return `${
                                      creditNoteJson?.currency_symbol || "₹"
                                    }${formatAmount(tdsAmount)}`;
                                  })()}
                                </span>
                                <span className="text-caption font-caption text-subtext-color">
                                  ({item.tds_tax_percentage || 0}%)
                                </span>
                              </div>
                            ) : (
                              <span className="text-body font-body text-subtext-color">
                                -
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex w-32 flex-none items-start">
                          <span className="text-body-bold font-body-bold text-default-font">
                            {creditNoteJson?.currency_symbol || "₹"}
                            {formatAmount(
                              isGstEnabled
                                ? (item.rate || 0) * (item.quantity || 0) +
                                    ((item.rate || 0) *
                                      (item.quantity || 0) *
                                      (item.tax_percentage || 0)) /
                                      100
                                : (item.rate || 0) * (item.quantity || 0),
                            )}
                          </span>
                        </div>
                        <div className="flex flex-1 items-center gap-1 justify-end">
                          <IconButton
                            size="small"
                            icon={<FeatherX />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {
                              event.stopPropagation();
                              handleDeleteLineItem(index);
                            }}
                            disabled={isEditingDisabled}
                          />
                          <IconButton
                            size="small"
                            icon={<FeatherEdit2 />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {
                              event.stopPropagation();
                              handleEditLineItem(index);
                            }}
                            disabled={isEditingDisabled}
                          />
                        </div>
                      </div>
                    ))
                  )}
                  {/* Totals */}
                  <div className="flex flex-col items-end gap-3 border-t border-solid border-neutral-200 bg-default-background px-4 py-4 w-full">
                    <div className="flex w-full max-w-[384px] items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Subtotal
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {creditNoteJson?.currency_symbol || "₹"}
                        {formatAmount(
                          lineItems.reduce(
                            (sum, item) =>
                              sum + (item.rate || 0) * (item.quantity || 0),
                            0,
                          ),
                        )}
                      </span>
                    </div>
                    {/* Tax Breakdown */}
                    {isGstEnabled &&
                      taxBreakdown.rows.map((row) => (
                        <div
                          key={row.label}
                          className="flex w-full max-w-[384px] items-center justify-between"
                        >
                          <span className="text-body font-body text-subtext-color">
                            {row.label}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {creditNoteJson?.currency_symbol || "₹"}
                            {formatAmount(row.amount)}
                          </span>
                        </div>
                      ))}
                    {/* TDS (for vendor credits only) */}
                    {isVendorCreditNote && isTDSApplicable && (
                      <div className="flex w-full max-w-[384px] items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          TDS Deducted
                        </span>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {creditNoteJson?.currency_symbol || "₹"}
                          {formatAmount(
                            lineItems.reduce((sum, item) => {
                              if (item.tds_tax_id && item.tds_tax_percentage) {
                                const itemSubTotal =
                                  (item.rate || 0) * (item.quantity || 0);
                                const tdsAmount =
                                  (itemSubTotal *
                                    (item.tds_tax_percentage || 0)) /
                                  100;
                                return sum + tdsAmount;
                              }
                              return sum;
                            }, 0),
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex w-full max-w-[384px] items-center justify-between border-t border-solid border-neutral-border pt-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Total Amount
                      </span>
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        {creditNoteJson?.currency_symbol || "₹"}
                        {(() => {
                          const subtotal = lineItems.reduce(
                            (sum, item) =>
                              sum + (item.rate || 0) * (item.quantity || 0),
                            0,
                          );
                          const totalTax = isGstEnabled
                            ? taxBreakdown.totalTax
                            : 0;
                          const totalTDS =
                            isVendorCreditNote && isTDSApplicable
                              ? lineItems.reduce((sum, item) => {
                                  if (
                                    item.tds_tax_id &&
                                    item.tds_tax_percentage
                                  ) {
                                    const itemSubTotal =
                                      (item.rate || 0) * (item.quantity || 0);
                                    const tdsAmount =
                                      (itemSubTotal *
                                        (item.tds_tax_percentage || 0)) /
                                      100;
                                    return sum + tdsAmount;
                                  }
                                  return sum;
                                }, 0)
                              : 0;

                          // Total = Subtotal + GST - TDS (TDS is deducted)
                          return formatAmount(subtotal + totalTax - totalTDS);
                        })()}
                      </span>
                    </div>
                    {/* Display total validation error */}
                    {errors.total && (
                      <div className="flex w-full max-w-[384px] items-start justify-end">
                        <span className="text-caption font-caption text-error-700 text-right">
                          {errors.total}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Log and Queries */}
          <div className="flex w-full items-start gap-6 px-6 py-6">
            {/* Show queries even if empty */}
            <QueriesListForDocument
              queries={queriesEntities}
              loading={queriesLoading}
              loadingMore={queriesLoadingMore}
              hasMore={queriesHasMore}
              onLoadMore={loadMoreQueries}
              onQueryClick={handleQueryClick}
            />

            {/* Activity Log - Show even if empty */}
            {documentId && (
              <div
                className={`flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 ${
                  queriesEntities.length > 0 || queriesLoading
                    ? "w-1/2"
                    : "w-full"
                }`}
              >
                <ActivityLogComponent
                  key={activityLogRefreshKey}
                  feature="credit_note"
                  featureId={documentId}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Credit Note Confirmation */}
      <DialogLayout
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
      >
        <div className="flex w-144 flex-col gap-6 rounded-lg border border-solid border-neutral-border bg-default-background px-8 py-6 shadow-lg">
          <div className="flex items-start gap-3">
            <FeatherTrash2 className="text-heading-3 font-heading-3 text-error-500" />
            <div className="flex grow flex-col gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Delete credit note?
              </span>
              <span className="text-body font-body text-subtext-color">
                This action cannot be undone. The credit note and its data will
                be permanently removed.
              </span>
            </div>
          </div>
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-tertiary"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={isDeleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive-primary"
              icon={<FeatherTrash2 />}
              onClick={handleConfirmDeleteCreditNote}
              disabled={isDeleteLoading}
            >
              {isDeleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </DialogLayout>

      {/* Delete Credit Applied Confirmation */}
      <DialogLayout
        open={isDeleteApplicationConfirmOpen}
        onOpenChange={setIsDeleteApplicationConfirmOpen}
      >
        <div className="flex w-144 flex-col gap-6 rounded-lg border border-solid border-neutral-border bg-default-background px-8 py-6 shadow-lg">
          <div className="flex items-start gap-3">
            <FeatherTrash2 className="text-heading-3 font-heading-3 text-error-500" />
            <div className="flex grow flex-col gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Remove applied credit?
              </span>
              <span className="text-body font-body text-subtext-color">
                This will remove the selected credit application from this{" "}
                {creditToDelete?.vendor_credit_id ? "bill" : "invoice"}.
              </span>
            </div>
          </div>
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-tertiary"
              onClick={() => setIsDeleteApplicationConfirmOpen(false)}
              disabled={isDeleteApplicationLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive-primary"
              icon={<FeatherTrash2 />}
              onClick={() =>
                handleConfirmDeleteCreditNoteApplication(
                  creditToDelete?.creditnote_id ||
                    creditToDelete?.vendor_credit_id ||
                    "",
                  creditToDelete || {},
                )
              }
              disabled={isDeleteApplicationLoading}
            >
              {isDeleteApplicationLoading ? "Removing..." : "Remove"}
            </Button>
          </div>
        </div>
      </DialogLayout>

      {/* Edit Line Item Modal */}
      <EditLineItemForCreditNoteComponent
        open={isEditLineItemModalOpen}
        onOpenChange={setIsEditLineItemModalOpen}
        editingLineItemData={editingLineItemData}
        editingLineItemIndex={editingLineItemIndex}
        originalAccountName={originalAccountName}
        taxes={[]}
        isNewItem={editingLineItemIndex === -1}
        isGstEnabled={isGstEnabled}
        isManualCreation={isManualCreation}
        hasLinkedDocument={
          !!(
            creditNoteJson?.linked_invoice_id || creditNoteJson?.linked_bill_id
          )
        }
        onSave={handleSaveLineItem}
        onCancel={handleCancelEdit}
        onLineItemDataChange={handleLineItemDataChange}
        creditNoteId={documentId}
      />

      {/* Query Raise Modal */}
      <QueryRaiseModal
        open={isQueryRaiseModalOpen}
        onOpenChange={setIsQueryRaiseModalOpen}
        sourceDoc={{
          type: "credit_note",
          id: parseInt(documentId || "0"),
          filename: fileName || "credit-note.pdf",
          amount:
            (creditNoteJson?.currency_symbol || "₹") +
            (creditNoteJson?.total || "0"),
          // IMPORTANT: Do not include viewUrl for credit_note - attachments are not supported for credit notes
        }}
        onQueryRaised={refetchQueries}
      />

      {/* Query Detail Modal */}
      <QueryDetailModal
        open={isQueryDetailModalOpen}
        onOpenChange={setIsQueryDetailModalOpen}
        queryId={selectedQueryId}
        onQueryResolved={refetchQueries}
        hideViewSourceButton={true}
      />

      {/* Closed month confirm modal - when non-Admin/CFO tries to save or approve in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={(open) => {
          setShowClosedMonthConfirmModal(open);
          if (!open) setPendingClosedMonthSource(null);
        }}
        entityLabel="Credit Note"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          const source = pendingClosedMonthSource;
          setPendingClosedMonthSource(null);

          if (source === "delete") {
            setPendingReviewAction({
              changeDetails: {
                closed_month: true,
                action_types: [
                  "delete_after_push_to_zoho",
                  ACTION_TYPE_MONTH_CLOSE,
                ],
                raw_json: omitSupplyFields(creditNoteJson ?? undefined),
                original_json: originalCreditNoteJson ?? undefined,
                field_changes: [],
              },
              disableSubmit: false,
              actionCode: ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO,
            });
          }
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Request Modal */}
      {(documentId || !isPostedToZoho) && pendingReviewAction && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.CREDIT_NOTE}
          actionCode={
            pendingReviewAction.actionCode ||
            (isEditMode
              ? ACTION_CODES.UPDATE_AFTER_PUSH
              : ACTION_CODES.PUSH_TO_ZOHO)
          }
          entityType="credit_note"
          entityId={documentId ? parseInt(documentId) : 0}
          documentNumber={creditNoteJson?.creditnote_number}
          changeDetails={pendingReviewAction.changeDetails}
          disableSubmit={pendingReviewAction.disableSubmit}
          skipNavigation={true}
          skipReviewRequest={!isPostedToZoho && !documentId} // Skip if document is not posted and has no ID (need to save as draft first)
          onSuccess={handleReviewModalSuccess}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            showError(error.message || "Failed to create review request");
          }}
        />
      )}
    </DefaultPageLayout>
  );
};

export default CreditNoteReviewPage;
